import {
  BookOpen,
  Briefcase,
  Building2,
  HeartPulse,
  Check,
  ClipboardList,
  Eye,
  History,
  BadgeCheck,
  GraduationCap,
  HeartHandshake,
  Users,
  IdCard,
  Folder,
  Info,
  KeyRound,
  UserRound,
  MapPin,
  Phone,
  Settings,
  Sparkles,
  Award,
  Contact,
  Scale,
  Gavel,
  PackageCheck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { EmptyState } from '../../../../components/EmptyState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ImagePreviewModal } from '../../../../components/ImagePreviewModal';
import { Menu } from '../../../../components/Menu';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { EditableAvatar } from '../../../../components/profile/EditableAvatar';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileNav, type ProfileNavEntry } from '../../../../components/profile/ProfileNav';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { findNavLabel } from '../../../../components/profile/profileSectionTitle';
import { ProfileShell } from '../../../../components/profile/ProfileShell';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { ServiceRecordModal } from '../components/ServiceRecordModal';
import { EndServiceRecordModal } from '../components/EndServiceRecordModal';
import { EditAddressModal } from '../components/sections/EditAddressModal';
import { EditEmploymentModal } from '../components/sections/EditEmploymentModal';
import { EditAccessLevelModal } from '../components/sections/EditAccessLevelModal';
import { EditHmoModal } from '../components/sections/EditHmoModal';
import { EditIdentificationModal } from '../components/sections/EditIdentificationModal';
import { EditMilitaryModal } from '../components/sections/EditMilitaryModal';
import { EditAccountLinkModal } from '../components/sections/EditAccountLinkModal';
import { EditPersonalModal } from '../components/sections/EditPersonalModal';
import { EditPhotoModal } from '../components/sections/EditPhotoModal';
import { ApiError } from '../../../../lib/apiError';
import { calendarAge, formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { personnelApi, personsApi } from '../personnelApi';
import {
  CITIZENSHIP_LABELS,
  CIVIL_STATUS_LABELS,
  DUAL_CITIZENSHIP_KIND_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  SEX_LABELS,
  ADDRESS_KIND,
  MILITARY_BRANCH_LABELS,
  MILITARY_SERVICE_STATUS_LABELS,
  type PersonnelAddress,
  type PersonnelServiceRecord,
  type PersonIdentity,
  type PersonnelDetail,
  type PersonnelEducation,
  type PersonnelEligibility,
  type PersonnelFamilyMember,
  type PersonnelSurveyAnswer,
  type PersonnelWorkExperience,
  type PersonnelVoluntaryWork,
  type PersonnelTraining,
  type PersonnelSpecialSkill,
  type PersonnelRecognition,
  type PersonnelReference,
  type PersonnelSaln,
  type PersonnelDisciplinaryRecord,
} from '../personnelTypes';
import { usePersonNames } from '../usePersonNames';
import { usePersonPhoto } from '../usePersonPhoto';
import { EducationPanel } from '../components/EducationPanel';
import { EditEducationModal } from '../components/EditEducationModal';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { FamilyPanel } from '../components/FamilyPanel';
import { EditFamilyMemberModal } from '../components/EditFamilyMemberModal';
import { EligibilityPanel } from '../components/EligibilityPanel';
import { EditEligibilityModal } from '../components/EditEligibilityModal';
import { WorkExperiencePanel } from '../components/WorkExperiencePanel';
import { EditWorkExperienceModal } from '../components/EditWorkExperienceModal';
import { VoluntaryWorkPanel } from '../components/VoluntaryWorkPanel';
import { EditVoluntaryWorkModal } from '../components/EditVoluntaryWorkModal';
import { TrainingPanel } from '../components/TrainingPanel';
import { EditTrainingModal } from '../components/EditTrainingModal';
import { SpecialSkillsPanel } from '../components/SpecialSkillsPanel';
import { EditSpecialSkillModal } from '../components/EditSpecialSkillModal';
import { RecognitionPanel } from '../components/RecognitionPanel';
import { EditRecognitionModal } from '../components/EditRecognitionModal';
import { ReferencesPanel } from '../components/ReferencesPanel';
import { EditReferenceModal } from '../components/EditReferenceModal';
import { SalnPanel } from '../components/SalnPanel';
import { EditSalnModal } from '../components/EditSalnModal';
import { DisciplinaryPanel } from '../components/DisciplinaryPanel';
import { EditDisciplinaryModal } from '../components/EditDisciplinaryModal';
import { SurveyPanel } from '../components/SurveyPanel';
import { EditSurveyAnswerModal } from '../components/EditSurveyAnswerModal';
import type { SurveyQuestion } from '../../survey/surveyTypes';
import { IssuedAssetsPanel } from '../../../logistics/inventory/components/IssuedAssetsPanel';
import { useToast } from '../../../../components/toastContext';
import { IssuanceModal } from '../../../logistics/inventory/components/IssuanceModal';
import { RecordReturnModal } from '../../../logistics/inventory/components/RecordReturnModal';
import { issuancesApi } from '../../../logistics/inventory/issuanceApi';
import type { IssuanceDetail, IssuedAssetLine } from '../../../logistics/inventory/issuanceTypes';

/*
  The panels ARE the create wizard's steps, in the same order — a reader who filled the form
  finds the record filed the way they entered it, and neither list can grow a section the
  other lacks without somebody noticing.

  `info` rather than `personal`, because CLAUDE.md fixes the first panel's name: every
  profile opens on Info, and Info is where the audit trail lives. What the Personal step
  collects is on `basic`, split out on 14 Sep 2026 — so here the mirror is the pair, not the
  one panel.
*/
/**
 * The sections that have a dedicated editor.
 *
 * They are the panels that came from a step of the create form, which is the rule: a field is
 * corrected where it was entered. `info` is missing because the Info panel holds two cards and
 * only one of them is editable — `personal` is that card, and `audit` is a record of what
 * happened and takes no Edit at any permission level.
 *
 * `account` edits the LINK — which account this employee signs in as, a column on THIS record.
 * The account's own fields stay the users module's, and the card links out to them: that is
 * the rule about a card over another module's record, not an exemption from having an Edit.
 *
 * The name and the date of birth are NOT a section of their own. They were, for about an
 * hour, and it put two Edits in one card — which asked the reader to know that a name is
 * stored in a different database before they could correct a phone number. They are part of
 * `personal`, and the modal drops them for a reader who cannot reveal them.
 */
type EditSection =
  | 'personal'
  | 'photo'
  | 'address'
  | 'identification'
  | 'military'
  | 'employment'
  | 'hmo'
  | 'account'
  | 'access-level';

type Panel =
  | 'info'
  | 'basic'
  | 'address'
  | 'identification'
  /*
    ONE key for the employment facts and the service record itself. They were two panels and
    are one — see the panel itself for why.

    The KEY stays `employment` after the 15 Sep 2026 rename to Service Record: it is the
    create wizard's step key too, and the panel-mirror rule keys the two together. The LABEL is
    what the reader sees, and it is the thing that changed.
  */
  | 'employment'
  | 'hmo'
  | 'account'
  | 'contacts'
  | 'family'
  | 'education'
  | 'eligibility'
  | 'work-experience'
  | 'voluntary-work'
  | 'training'
  | 'special-skills'
  | 'recognition'
  | 'references'
  | 'survey'
  | 'saln'
  | 'disciplinary'
  | 'issued-assets';

/**
 * One employee's record — the sidebar layout, because a 201 file is the case CLAUDE.md names
 * for it: more about a person than one screen holds, and growing.
 *
 * **The heading is a name this page does not have.** The record comes from `rsys_` carrying a
 * `personId`; the name comes from the identity store, resolved through the same batch call the
 * list uses — a batch of one here. That is the composition §4.2 prescribes, and it is why the
 * identity block can render before the name arrives and fill it in after.
 */
export function PersonnelProfilePage() {
  const { id = '' } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();

  const [record, setRecord] = useState<PersonnelDetail | null>(null);
  /** A request is in flight. What that LOOKS like depends on whether a record is up — below. */
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  /*
    Opens on `info`. Product call, 15 Sep 2026, reversing the 14 Sep call that opened on
    `basic` — the reader arriving at an employee wants the employee, so `Basic info` was made
    the landing panel.

    What that produced was a rail nobody could read on arrival. `Basic info` lives inside the
    `201 File` group, which starts closed, so the page landed INSIDE a collapsed group: the
    group header wore the selection with every one of its entries hidden behind it, and the one
    thing the sidebar exists to show — which sections this record has — was the one thing it
    did not.

    `info` sits outside the group, so the rail now arrives genuinely collapsed: `Info`
    selected, `201 File` shut and carrying no selection of its own. `Basic info` is one click
    away, and the audit trail being first is the older rule the split was always measured
    against.
  */
  const [panel, setPanel] = useState<Panel>('info');
  const [reloadKey, setReloadKey] = useState(0);
  const [recordingService, setRecordingService] = useState(false);
  const [previewingPhoto, setPreviewingPhoto] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ending, setEnding] = useState<PersonnelServiceRecord | null>(null);

  /*
    Education, as two pieces of state rather than one: the modal is open for a line or for a
    new one, and the delete confirmation is a different question about a different row. A
    single discriminant would make "editing nothing" and "deleting something" the same shape.

    The editing state wraps the line in an object so that null (no modal) and { education:
    null } (adding one) stay distinguishable — the same reason `editing` is nullable on the
    section modals rather than a separate boolean.
  */
  const [educationEditing, setEducationEditing] = useState<{
    education: PersonnelEducation | null;
  } | null>(null);

  const [educationDeleting, setEducationDeleting] = useState<PersonnelEducation | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [educationBulkDeleting, setEducationBulkDeleting] = useState<
    PersonnelEducation[] | null
  >(null);

  const [eligibilityEditing, setEligibilityEditing] = useState<{
    row: PersonnelEligibility | null;
  } | null>(null);

  const [eligibilityDeleting, setEligibilityDeleting] = useState<PersonnelEligibility | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [eligibilityBulkDeleting, setEligibilityBulkDeleting] = useState<
    PersonnelEligibility[] | null
  >(null);

  const [workExperienceEditing, setWorkExperienceEditing] = useState<{
    row: PersonnelWorkExperience | null;
  } | null>(null);

  const [workExperienceDeleting, setWorkExperienceDeleting] =
    useState<PersonnelWorkExperience | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [workExperienceBulkDeleting, setWorkExperienceBulkDeleting] = useState<
    PersonnelWorkExperience[] | null
  >(null);

  const [voluntaryWorkEditing, setVoluntaryWorkEditing] = useState<{
    row: PersonnelVoluntaryWork | null;
  } | null>(null);

  const [voluntaryWorkDeleting, setVoluntaryWorkDeleting] = useState<PersonnelVoluntaryWork | null>(
    null,
  );

  /*
    The bulk delete is its own state rather than a flavour of `voluntaryWorkDeleting`: one row
    and a selection are different confirmations with different sentences, and folding them
    together is how a dialog ends up naming one record while deleting five.
  */
  const [voluntaryWorkBulkDeleting, setVoluntaryWorkBulkDeleting] = useState<
    PersonnelVoluntaryWork[] | null
  >(null);

  const [trainingEditing, setTrainingEditing] = useState<{
    row: PersonnelTraining | null;
  } | null>(null);

  const [trainingDeleting, setTrainingDeleting] = useState<PersonnelTraining | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [trainingBulkDeleting, setTrainingBulkDeleting] = useState<PersonnelTraining[] | null>(
    null,
  );

  const [specialSkillEditing, setSpecialSkillEditing] = useState<{
    row: PersonnelSpecialSkill | null;
  } | null>(null);

  const [specialSkillDeleting, setSpecialSkillDeleting] = useState<PersonnelSpecialSkill | null>(
    null,
  );

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [specialSkillBulkDeleting, setSpecialSkillBulkDeleting] = useState<
    PersonnelSpecialSkill[] | null
  >(null);

  const [recognitionEditing, setRecognitionEditing] = useState<{
    row: PersonnelRecognition | null;
  } | null>(null);

  const [recognitionDeleting, setRecognitionDeleting] = useState<PersonnelRecognition | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [recognitionBulkDeleting, setRecognitionBulkDeleting] = useState<
    PersonnelRecognition[] | null
  >(null);

  const [referenceEditing, setReferenceEditing] = useState<{
    row: PersonnelReference | null;
  } | null>(null);

  const [referenceDeleting, setReferenceDeleting] = useState<PersonnelReference | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [disciplinaryEditing, setDisciplinaryEditing] = useState<{
    row: PersonnelDisciplinaryRecord | null;
  } | null>(null);
  const [disciplinaryDeleting, setDisciplinaryDeleting] =
    useState<PersonnelDisciplinaryRecord | null>(null);
  const [disciplinaryBulkDeleting, setDisciplinaryBulkDeleting] = useState<
    PersonnelDisciplinaryRecord[] | null
  >(null);

  const [salnEditing, setSalnEditing] = useState<{ row: PersonnelSaln | null } | null>(null);
  const [salnDeleting, setSalnDeleting] = useState<PersonnelSaln | null>(null);
  const [salnBulkDeleting, setSalnBulkDeleting] = useState<PersonnelSaln[] | null>(null);

  const [referenceBulkDeleting, setReferenceBulkDeleting] = useState<
    PersonnelReference[] | null
  >(null);

  /*
    The survey answers one QUESTION at a time, so the open modal carries the question it was
    opened for and the answer already on file — null where there is none. There is no "adding"
    state to distinguish: every row of that panel is a question, answered or not.
  */
  const [surveyEditing, setSurveyEditing] = useState<{
    question: SurveyQuestion;
    answer: PersonnelSurveyAnswer | null;
  } | null>(null);

  const [familyEditing, setFamilyEditing] = useState<{
    member: PersonnelFamilyMember | null;
  } | null>(null);

  const [familyDeleting, setFamilyDeleting] = useState<PersonnelFamilyMember | null>(null);

  /* Its own state, for the reason the voluntary-work bulk delete has one. */
  const [familyBulkDeleting, setFamilyBulkDeleting] = useState<
    PersonnelFamilyMember[] | null
  >(null);

  /*
    Which section is being edited, as one discriminant rather than a boolean per card — the
    arrangement `<UserProfilePage>` already uses. Two booleans can both be true; this cannot,
    and a modal is open for exactly one section or none.
  */
  const [editing, setEditing] = useState<EditSection | null>(null);

  /*
    ISSUED ASSETS — Logistics records, fetched on their OWN call rather than riding on the
    personnel detail. Two reasons, and the second is the one that decides it: the rows belong
    to another module and come from that module's endpoint; and a count of them carried on the
    record would disclose, to every reader of a personnel profile, how much property somebody
    holds — the count would be gated on `personnel.read` while the rows sit behind
    `issuances.read`. So the fetch, the nav badge and the panel share ONE gate.

    EAGER, not on panel open, because the nav badge is the count of these rows and a badge must
    render before the panel is clicked. Null while in flight; the panel skeletons.
  */
  const canReadIssuances = hasPermission('issuances.read');
  const toast = useToast();
  const [issuedAssets, setIssuedAssets] = useState<IssuedAssetLine[] | null>(null);
  const [issuedAssetsFailed, setIssuedAssetsFailed] = useState<string | null>(null);
  const [issuedAssetsReloadKey, setIssuedAssetsReloadKey] = useState(0);
  const [issuingAsset, setIssuingAsset] = useState(false);

  /** The slip a return is being recorded against, fetched whole — the modal needs its lines. */
  const [returningAsset, setReturningAsset] = useState<{
    issuance: IssuanceDetail;
    lineId: string;
  } | null>(null);

  useEffect(() => {
    setIssuedAssets(null);
    setIssuedAssetsFailed(null);
  }, [id]);

  useEffect(() => {
    if (!canReadIssuances) return;

    const controller = new AbortController();

    issuancesApi
      .issuedTo(id, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setIssuedAssets(next);
        setIssuedAssetsFailed(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setIssuedAssetsFailed(
          caught instanceof ApiError ? caught.message : 'Could not load the issued assets.',
        );
      });

    return () => controller.abort();
  }, [id, canReadIssuances, issuedAssetsReloadKey]);

  async function openReturn(row: IssuedAssetLine) {
    try {
      const issuance = await issuancesApi.get(row.issuanceId);
      setReturningAsset({ issuance, lineId: row.id });
    } catch (caught) {
      // No form is open yet to report in, and the list itself is fine — so a toast, which is
      // where an outcome with nowhere else to go is said. Never the panel's load error.
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not open that issuance.');
    }
  }

  /*
    One FETCH for the whole page, and that part is not negotiable: an appointment changes the
    service-record count in the nav AND the post line in the header, so a panel that refetched
    only itself would leave the rest of the page stating the previous answer — the badge
    fan-out rule.

    What is per-panel is the LOADING TREATMENT. Blanking the header, the nav and the
    breadcrumb to skeletons because a row was added three panels down throws away everything
    the reader was looking at to report progress on one part of it.
  */
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    A different subject is a different page, so the record on screen is dropped and the reader
    gets the first-load treatment. Without this the previous employee stays up — under the new
    one's breadcrumb — until the fetch lands.

    Declared BEFORE the fetch effect so it runs first in the same commit.
  */
  useEffect(() => {
    setRecord(null);
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setFetching(true);

    personnelApi
      .get(id, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setRecord(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this record.', 0),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setFetching(false);
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  /*
    Two different states, and the distinction is the whole point:

    `loading`    nothing to show yet — the page is skeletons.
    `refreshing` something IS on screen and a newer copy is coming. The header, the nav and
                 the breadcrumb keep the answer they have; only the open panel skeletons,
                 because it is the one whose content the write actually changed.

    Derived rather than stored, so they cannot contradict `record`.
  */
  const loading = record === null && error === null;
  const refreshing = fetching && record !== null;

  /*
    A batch of one, through the same hook the list uses. Resolving it here rather than with a
    bespoke call is what keeps there being ONE way a name reaches the screen — a second path
    is a second thing to keep in step when the store moves.
  */
  const names = usePersonNames(record ? [record.personId] : []);

  /*
    The photograph is the identity store's fifth field, so it is fetched by `PersonId` from
    `rsys_pii` rather than arriving on the record — there is no copy of it in `rsys_` and
    there must not be. It reloads on its own after a write, because nothing else on this
    page changes when a face does.
  */
  const photo = usePersonPhoto(record?.personId ?? null);

  const displayName = record
    ? (names.nameOf(record.personId) ?? (names.failed ? 'Name unavailable' : record.employeeNumber))
    : '';

  const erased = record ? names.statusOf(record.personId) === 'erased' : false;

  /*
    Falsy, not `=== null`. The API omits null properties entirely, so an open service record
    arrives with no `effectiveTo` key at all — an identity check against null is false for
    every open row, which read as "No post held" on someone who had just been appointed.
  */
  const openServiceRecords =
    record?.serviceRecords.filter((a) => a.isActive && !a.effectiveTo) ?? [];

  /*
    The PRIMARY open line — what the record reads its unit, its grade and its reporting line
    from, and what payroll computes against.

    NOT `openServiceRecords[0]`, which is what the header used to show. An employee may hold
    several posts at once and exactly one of them is primary, so the first open row is whichever
    the sort happened to put first: a designation on top of a permanent appointment answered for
    the substantive one. Null is a real state — every open line can be secondary once the
    primary one has ended.
  */
  const primaryServiceRecord = openServiceRecords.find((a) => a.isPrimary) ?? null;

  const contacts = record?.emergencyContacts ?? [];

  const entries: ProfileNavEntry[] = [
    {
      key: 'info',
      label: 'Info',
      icon: <Info className="h-4 w-4" aria-hidden="true" />,
    },
    {
      /*
        Everything below Info, under the name the document actually has. A 201 file IS these
        sections — personal, family, education, eligibility, work, voluntary work, the
        questionnaire — so the group is not a tidying device, it is what the reader calls the
        thing they are reading.

        `defaultOpen: true`. Product call, 15 Sep 2026, reversing the call made earlier the
        same day: it started CLOSED, on the argument that a dozen-odd entries opening at full
        length hands the reader the whole form before they have asked for any of it.

        Shut, the rail showed two rows — `Info` and a folder — which is a panel picker that
        picks nothing. The sections ARE what the reader came to the sidebar for, and the cost
        of listing them is a scroll rather than a wrong answer. Collapsing is still one click
        away, and `<ProfileNav>` no longer forces a group open, so it stays shut once shut.
      */
      key: '201-file',
      label: '201 File',
      icon: <Folder className="h-4 w-4" aria-hidden="true" />,
      defaultOpen: true,
      items: [
        {
          key: 'basic',
          label: 'Basic info',
          icon: <UserRound className="h-4 w-4" aria-hidden="true" />,
        },
        {
          /*
            Directly under Basic info, and inside the file. Product call, 15 Sep 2026 —
            reversing a call made hours earlier that put this outside the group entirely.

            Both of these are about the PERSON rather than their employment or the form:
            who to call, and what card covers them. They read next to the basic facts, which
            is where somebody looking for either of them goes first.
          */
          key: 'contacts',
          label: 'Emergency contacts',
          icon: <Phone className="h-4 w-4" aria-hidden="true" />,
          count: contacts.length,
        },
        {
          key: 'hmo',
          label: 'HMO',
          icon: <HeartPulse className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'address',
          label: 'Addresses',
          icon: <MapPin className="h-4 w-4" aria-hidden="true" />,
          count: record?.addresses.length ?? 0,
        },
        {
          key: 'identification',
          label: 'Identification',
          icon: <IdCard className="h-4 w-4" aria-hidden="true" />,
          count: record?.identifications.length ?? 0,
        },
        /*
          Military has no entry of its own. It is a SECTION of Basic info — one card inside
          that panel, drawn only for a record that claims service. Product call, 17 Sep 2026.

          The panel-mirror rule survives it: the mirror is about the SET, and a step and a
          panel need not be one to one — the Service Record panel already answers two steps'
          worth of question in one place. What the rule still guarantees holds here, because
          the Military step collects nothing the profile cannot show.

          What it buys is a rail that stops changing shape per record: the entry appeared and
          vanished depending on a flag, so two employees' profiles offered different panel
          lists and a reader learned a position that moved.
        */
        {
          /*
            ONE entry for what were two. The employment facts and the posts held are the same
            question asked twice — what this person does here — and a reader correcting a hire
            date and a reader recording a new line of service were being sent to different panels
            for it. Product call, 15 Sep 2026.

            **It is the SERVICE RECORD**, renamed the same day from `Employment/Assignments`.
            That is what a Philippine employer calls this section of a 201 file, and a panel
            named after two of its parts joined by a slash was naming the implementation rather
            than the document.

            The count is the lines of service, because that is the part that grows; the
            employment card is one fixed set of facts and has no count to give.
          */
          key: 'employment',
          label: 'Service Record',
          icon: <Briefcase className="h-4 w-4" aria-hidden="true" />,
          count: record?.serviceRecords.length ?? 0,
        },

        {
          /*
          Above Education, because section II comes before section III on the form and a 201
          file is read in the form's order.

          The count is the LIVE members. Deleted ones still travel with the record so the
          panel's Show deleted has something to lift, but a nav count that included them would
          tell the reader there is more here than there is.
        */
          key: 'family',
          label: 'Family background',
          icon: <Users className="h-4 w-4" aria-hidden="true" />,
          count: record?.familyMembers.filter((m) => m.isActive).length ?? 0,
        },
        {
          /*
          Last, below every other entry. The panels above it are who someone is and how they
          are employed; educational background is a section of the 201 file read on its own,
          and it is the newest — a nav that reorders itself as sections are added moves the
          entries a reader has already learned the position of.

          The count renders even at zero, which is the ProfileNav rule: it is what lets a
          reader see which panels hold anything before clicking.
        */
          key: 'education',
          label: 'Education',
          icon: <GraduationCap className="h-4 w-4" aria-hidden="true" />,
          count: record?.educations.filter((e) => e.isActive).length ?? 0,
        },
        {
          /*
          After Education, because section IV follows section III on the form and a 201 file is
          read in the form's order.
        */
          key: 'eligibility',
          label: 'Eligibilities',
          icon: <BadgeCheck className="h-4 w-4" aria-hidden="true" />,
          count: record?.eligibilities.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
          After Eligibility, for the same reason Eligibility follows Education: section V comes
          after section IV on the form, and a 201 file is read in the form's order.

          NOT the briefcase, which the Service Record already carries — and the two are easy to confuse.
          A service record is the plantilla item this organization has them occupying now; this is
          every job they have held, most of them somewhere else. The building says "places
          worked" where the briefcase says "the post here".
        */
          key: 'work-experience',
          label: 'Work experience',
          icon: <Building2 className="h-4 w-4" aria-hidden="true" />,
          count: record?.workExperiences.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
          After Work experience, because section VI follows section V on the form and a 201 file
          is read in the form's order.

          The handshake rather than another building: this is the one history on the record that
          is not employment, and the pair of entries above it — the briefcase for the post held
          here, the building for posts held elsewhere — are both about being employed. An icon
          that said "organization" a third time would put the reader's eye on the wrong
          distinction.
        */
          key: 'voluntary-work',
          label: 'Voluntary work',
          icon: <HeartHandshake className="h-4 w-4" aria-hidden="true" />,
          count: record?.voluntaryWorks.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            After Voluntary work, because section VII follows section VI on the form and a 201
            file is read in the form's order.

            The graduation cap is Education's, so this takes the book: the two are both about
            learning and the reader has to tell them apart at a glance — Education is where
            somebody studied, this is what the employer sent them on.
          */
          key: 'training',
          label: 'Training/L&D',
          icon: <BookOpen className="h-4 w-4" aria-hidden="true" />,
          count: record?.trainings.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            After Training/L&D, because item 31 opens section VIII and section VIII follows
            section VII on the form — a 201 file is read in the form's order. It is BEFORE the
            Survey, whose items are 34 to 40, so this is an append within the form's run rather
            than a slot-in: nothing a reader has already learned the position of moves.

            The sparkles rather than another book or cap: Education is where somebody studied,
            Training/L&D is what the employer sent them on, and this is what they can do — an
            icon about learning a third time would put the reader's eye on the wrong distinction.
          */
          key: 'special-skills',
          label: 'Special skills/Hobbies',
          icon: <Sparkles className="h-4 w-4" aria-hidden="true" />,
          count: record?.specialSkills.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            DIRECTLY BELOW Special skills/Hobbies, because item 32 follows item 31 on the form and
            a 201 file is read in the form's order. An append within the run rather than a
            slot-in: nothing a reader has already learned the position of moves.

            The award ribbon rather than another star: Sparkles above is what somebody can DO, and
            this is what somebody was GIVEN for doing it — a second sparkle would put the reader's
            eye on the wrong distinction, which is the call the Training/L&D icon already makes
            against Education's cap.
          */
          key: 'recognition',
          label: 'Recognitions',
          icon: <Award className="h-4 w-4" aria-hidden="true" />,
          count: record?.recognitions.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            DIRECTLY BELOW Recognition, as asked. Worth knowing: the PDS prints its references
            block near the END of the form, after the questionnaire, so strict form order would
            put this after Survey rather than before it. Product call, 17 Sep 2026 — the panels
            either side of it are the other two "other information" sections, and a reader looking
            for who can vouch for somebody looks near what they are good at and what they were
            given, not past the criminal-record questions.

            The contact card rather than another person glyph: Family background and Emergency
            contacts already use Users and Phone, and a referee is neither kin nor the person you
            ring in an emergency — they are a name an employer follows up.
          */
          key: 'references',
          label: 'References',
          icon: <Contact className="h-4 w-4" aria-hidden="true" />,
          count: record?.references.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
          LAST, and appended rather than slotted in: items 34 to 40 come after section V on the
          form, and a 201 file is read in the form's order. Appending also keeps the position of
          every entry a reader has already learned.

          The count is the answers GIVEN, and this is the one nav badge that is not the number
          of rows behind it — the panel lists every question, so a row count would read 12 on
          every record and say nothing. What the badge is for is "does this panel hold anything
          yet", and only the answers can tell a reader that.
        */
          key: 'survey',
          label: 'Survey',
          icon: <ClipboardList className="h-4 w-4" aria-hidden="true" />,
          count: record?.surveyAnswers.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            LAST, appended after the form's own run — and INSIDE the group, which is the part
            worth stating because a SALN is not a section of CS Form 212 at all.

            The membership test is about the DOCUMENT, not the printed form: Emergency contacts
            and HMO are on neither the PDS nor the wizard and sit inside the group, because they
            are facts a 201 file holds. A SALN is the same shape one step further out — its own
            statutory filing under RA 6713, and one every Philippine 201 file carries. Being
            absent from the form is not the same as being outside the file.

            So it appends after Survey rather than being slotted among the form's sections: the
            form's order governs the form's sections, and this one is not among them.

            The BALANCE, because that is literally what the document is — assets weighed against
            liabilities, ending in a net worth. Not a wallet or a coin, which would say
            "money" about a disclosure whose subject is the balance rather than the amount.
          */
          key: 'saln',
          label: 'SALN filings',
          icon: <Scale className="h-4 w-4" aria-hidden="true" />,
          count: record?.salns.filter((x) => x.isActive).length ?? 0,
        },
        {
          /*
            LAST, appended after SALN — and inside the group for the same reason SALN is: the
            membership test is the DOCUMENT rather than the printed form, and a 201 file holds
            a disciplinary history whether or not CS Form 212 asks for one.

            The GAVEL, because what this section records is a proceeding and its outcome. Not
            a warning triangle, which would mark the whole panel as a problem on every record
            that has one — a dismissed case is a good outcome, and an entry that shouts is one
            a reader learns to skip.
          */
          key: 'disciplinary',
          label: 'Disciplinary records',
          icon: <Gavel className="h-4 w-4" aria-hidden="true" />,
          count: record?.disciplinaryRecords.filter((x) => x.isActive).length ?? 0,
        },
      ],
    },
    {
      /*
        ISSUED ASSETS — OUTSIDE the group, ABOVE Account. Product call, 06 Oct 2026.

        Not a section of the 201 file: it is Logistics' accountability register read for one
        person — what property they have signed for on an ICS or a PAR. Filing it under
        `201 File` would claim a custody slip is a section of the personnel document, which is
        the test Account already failed for the same reason. It sits with Account as the other
        adjunct: a record another module keeps about this employee.

        Gated on `issuances.read` — the key of the rows it shows, never a personnel key — so
        a reader without it meets neither the entry nor the fetch. The count is the LIVE lines;
        deleted slips are still fetched so Show deleted has something to lift.
      */
      key: 'issued-assets',
      label: 'Issued Assets',
      icon: <PackageCheck className="h-4 w-4" aria-hidden="true" />,
      count: issuedAssets?.filter((x) => x.isActive).length ?? 0,
      permission: 'issuances.read',
    },
    {
      /*
        OUTSIDE the group, after it. A 201 file is a document with named sections, and this is
        not one of them: it is the account the employee signs in as — another module's record,
        which this one holds a `userId` for. Filing it under `201 File` said the sign-in
        credential was a section of the personnel form, which it has never been.

        After the group rather than before it, because `Info` is the record's provenance and
        the document is the record; an adjunct to both reads last.
      */
      key: 'account',
      label: 'Account',
      icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
      // The account is another module's record, so reading it takes that module's key.
      permission: 'users.read',
    },
  ];

  /*
    ABOVE THE READER'S ACCESS LEVEL — its own state, tested before the generic failure below.

    The list told this reader the record exists and counted it, so arriving here and being told
    "something went wrong" would contradict the screen they came from. It says what is true: the
    file is classified, and their clearance does not reach it. The breadcrumb is the way back.
  */
  if (error?.isForbidden && !record) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb
          segments={['Human Resources', { label: 'Personnel', to: '/personnel' }, 'Record']}
        />
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            message={
              <span className="flex flex-col items-center gap-1">
                <span className="text-slate-600">This record is above your access level.</span>
                <span className="text-xs text-slate-500">
                  It is classified, and your clearance does not reach it. Someone who may set
                  access levels can change that.
                </span>
              </span>
            }
          />
        </div>
      </div>
    );
  }

  /*
    The full-page error is for a failure with NOTHING to show — the record never arrived, so
    there is no page to keep. A failed REFRESH is a different event: a record is on screen and
    it is still the last good answer, so replacing it with an error state throws away what the
    reader has in order to report that it might be a few seconds stale. That one goes to the
    banner instead, which is what `<ProfileShell>`'s banner slot is for.
  */
  if (error && !record) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb
          segments={['Human Resources', { label: 'Personnel', to: '/personnel' }, 'Record']}
        />
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState message={error.message} />
        </div>
      </div>
    );
  }

  /*
    Restore has no dialog, and that is the rule rather than an omission: an undo puts
    something back, and ceremony on it just trains people to click through ceremony. Delete
    gets the confirmation; restore runs.
  */
  /*
    Restores the EMPLOYMENT row. The identity was never deactivated when the member was
    deleted, so there is nothing to bring back on the other side of the seam — see
    DeleteFamilyMemberAsync for why that is the right half to flag.
  */
  async function restoreFamilyMember(member: PersonnelFamilyMember) {
    if (!record) return;

    try {
      await personnelApi.restoreFamilyMember(record.id, member.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that member.', 0),
      );
    }
  }

  async function restoreEligibility(row: PersonnelEligibility) {
    if (!record) return;

    try {
      await personnelApi.restoreEligibility(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreWorkExperience(row: PersonnelWorkExperience) {
    if (!record) return;

    try {
      await personnelApi.restoreWorkExperience(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreVoluntaryWork(row: PersonnelVoluntaryWork) {
    if (!record) return;

    try {
      await personnelApi.restoreVoluntaryWork(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreTraining(row: PersonnelTraining) {
    if (!record) return;

    try {
      await personnelApi.restoreTraining(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreSpecialSkill(row: PersonnelSpecialSkill) {
    if (!record) return;

    try {
      await personnelApi.restoreSpecialSkill(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreRecognition(row: PersonnelRecognition) {
    if (!record) return;

    try {
      await personnelApi.restoreRecognition(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreDisciplinaryRecord(row: PersonnelDisciplinaryRecord) {
    if (!record) return;

    try {
      await personnelApi.restoreDisciplinaryRecord(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreSaln(row: PersonnelSaln) {
    if (!record) return;

    try {
      await personnelApi.restoreSaln(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreReference(row: PersonnelReference) {
    if (!record) return;

    try {
      await personnelApi.restoreReference(record.id, row.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that record.', 0),
      );
    }
  }

  async function restoreEducation(education: PersonnelEducation) {
    if (!record) return;

    try {
      await personnelApi.restoreEducation(record.id, education.id);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore that line.', 0),
      );
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Breadcrumb
        segments={[
          'Human Resources',
          { label: 'Personnel', to: '/personnel' },
          loading ? 'Loading' : displayName,
        ]}
      />

      <div className="min-h-0 flex-1">
        <ProfileShell
          /* The heading every panel below states. Resolved from the nav rather than
             typed, so the rail and the heading cannot name the section differently. */
          sectionTitle={findNavLabel(entries, panel)}
          id="personnel-profile"
          /*
            A refresh that failed, over a record that is still up. It says what is on screen
            may be stale rather than pretending the page is fine — and it sits above the panel
            on every panel, because a stale record is stale on all of them.
          */
          banner={
            error && record ? (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs text-red-700">
                  {error.message} What is shown is the last copy that loaded.
                </p>
              </div>
            ) : undefined
          }
          /*
            The identity strip, full width, above both columns. It carries what the sidebar
            block used to — avatar, name, status — so `<ProfileIdentity>` is GONE from the
            sidebar rather than sitting under it: one record printed twice on one screen is
            the rule this page would otherwise break. The sidebar is the panel picker now.
          */
          header={
            loading || !record ? (
              <div className="flex shrink-0 items-center gap-4 border-b border-slate-200 bg-white px-4 py-3.5">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-3 w-80" />
                </div>
              </div>
            ) : (
              <ProfileHeader
                id="personnel-identity"
                avatar={
                  <EditableAvatar
                    id="personnel-avatar"
                    name={displayName}
                    email={record.email}
                    imageUrl={photo.url}
                    size="xl"
                    /*
                      Hidden rather than disabled for a reader who cannot write one: passing
                      nothing is what removes the entry.
                    */
                    onEdit={
                      hasPermission('personnel.update') ? () => setEditing('photo') : undefined
                    }
                    /*
                      Looking at the photograph needs no key beyond the one that opened this
                      page: the avatar is already on screen, and enlarging it reveals nothing
                      the reader cannot already see.
                    */
                    onPreview={() => setPreviewingPhoto(true)}
                  />
                }
                title={displayName}
                /*
                  The facts that need no label, joined with `·` by the header itself. The
                  employee number is a machine value and takes the strip's mono face; the post
                  is a NAME, so it opts back into the body face — a name is not a code however
                  code-like the row around it looks.
                */
                meta={[
                  record.employeeNumber,
                  primaryServiceRecord ? (
                    <span key="post" className="font-sans">
                      {primaryServiceRecord.orgUnitName} — {primaryServiceRecord.positionTitle}
                    </span>
                  ) : (
                    <span key="post" className="font-sans text-slate-400">
                      No primary post
                    </span>
                  ),
                ]}
                /*
                  Its own line: an address is long enough to push the post off the first one.

                  The employment facts moved here from the Service Record panel's own card on
                  15 Sep 2026 — see `<ServiceRecordPanel>`. They are record-level and true on
                  every panel, exactly like the employee number and the post above them, and the
                  header is where a fact like that belongs rather than in one panel's card.
                */
                secondary={[
                  record.email,
                  record.employmentType ? EMPLOYMENT_TYPE_LABELS[record.employmentType] : null,

                  // A CALENDAR date, so the conversion-free formatter — and labelled, because
                  // a bare date beside an email says nothing about which date it is.
                  record.dateHired ? `Hired ${formatDateOnly(record.dateHired)}` : null,
                ]}
                status={
                  <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                    {record.isActive ? 'active' : 'inactive'}
                  </StatusText>
                }
                /*
                  Both pairs: the line states the LATEST change, and falls back to the creation
                  for a record nobody has touched since. The status above it is what it is
                  because of the last thing that happened — and on a record where that is still
                  its creation, the creator IS the attribution rather than a blank.
                */
                createdBy={record.createdByName}
                createdAt={record.createdAt}
                /*
                  Beside the status, because both are about the whole record. History Logs is
                  scoped to THIS record by `entityId` — the module's own trail is on the index
                  page, and a profile asking "what happened to this one" wants only its rows.

                  Gated HERE, not only on the entry. `<Menu>` filters its items by permission
                  but renders whatever trigger it is handed, so a reader holding neither key
                  would get a cog that opens an empty panel — a control that can never do
                  anything, which is the noise the hide-rule exists to prevent.

                  ANY OF the two, which is what the earlier note here said would happen the
                  moment a second entry arrived. It arrived on 15 Sep 2026: Edit employment
                  followed its facts out of the Service Record panel's card and into this cog,
                  because an action belongs with the thing it changes and those facts are now
                  stated in the strip above.
                */
                actions={
                  hasPermission('audit-logs.read') || hasPermission('personnel.update') ? (
                    <Menu
                      id="personnel-profile-settings"
                      items={[
                        {
                          key: 'edit-employment',
                          label: 'Edit employment',
                          icon: <Briefcase className="h-4 w-4" aria-hidden="true" />,
                          onClick: () => setEditing('employment'),
                          permission: 'personnel.update',
                        },
                        {
                          key: 'history',
                          label: 'History Logs',
                          icon: <History className="h-4 w-4" aria-hidden="true" />,
                          onClick: () => setHistoryOpen(true),
                          permission: 'audit-logs.read',
                        },
                      ]}
                      trigger={({ open, toggle }) => (
                        <button
                          type="button"
                          onClick={toggle}
                          aria-haspopup="menu"
                          aria-expanded={open}
                          aria-label="Record settings"
                          className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                        >
                          <Settings className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    />
                  ) : null
                }
              />
            )
          }
          sidebar={
            <>
              <ProfileNav
                id="personnel-nav"
                entries={entries}
                activeKey={panel}
                onSelect={(key) => setPanel(key as Panel)}
                hasPermission={hasPermission}
              />
            </>
          }
        >
          {/*
            The panel skeletons on a FIRST load and on a refresh alike — the difference is
            what happens around it. On a first load the header and the nav are skeletons too;
            on a refresh they keep the answer they have, because a write three panels down
            did not make the person's name unknown.
          */}
          {loading || refreshing || !record ? (
            <PanelSkeleton />
          ) : (
            <>
              {panel === 'info' ? (
                <InfoPanel record={record} onEditAccessLevel={() => setEditing('access-level')} />
              ) : null}

              {panel === 'basic' ? (
                <BasicInfoPanel
                  record={record}
                  erased={erased}
                  onEdit={() => setEditing('personal')}
                  onEditMilitary={() => setEditing('military')}
                />
              ) : null}

              {panel === 'address' ? (
                <AddressPanel record={record} onEdit={() => setEditing('address')} />
              ) : null}

              {panel === 'identification' ? (
                <IdentificationPanel record={record} onEdit={() => setEditing('identification')} />
              ) : null}

              {panel === 'employment' ? (
                <ServiceRecordPanel
                  record={record}
                  primary={primaryServiceRecord}
                  openCount={openServiceRecords.length}
                  onRecord={() => setRecordingService(true)}
                  onEnd={setEnding}
                />
              ) : null}

              {panel === 'hmo' ? (
                <HmoPanel record={record} onEdit={() => setEditing('hmo')} />
              ) : null}

              {panel === 'issued-assets' ? (
                <IssuedAssetsPanel
                  id="personnel-issued-assets"
                  rows={issuedAssets}
                  failed={issuedAssetsFailed}
                  employeeIsActive={record.isActive}
                  onIssue={() => setIssuingAsset(true)}
                  onReturn={(row) => void openReturn(row)}
                />
              ) : null}

              {panel === 'account' ? (
                <AccountPanel record={record} onEdit={() => setEditing('account')} />
              ) : null}

              {panel === 'contacts' ? <ContactsPanel record={record} /> : null}

              {panel === 'family' ? (
                <FamilyPanel
                  record={record}
                  onAdd={() => setFamilyEditing({ member: null })}
                  onEdit={(member) => setFamilyEditing({ member })}
                  onDelete={(member) => setFamilyDeleting(member)}
                  onBulkDelete={(members) => setFamilyBulkDeleting(members)}
                  onRestore={(member) => void restoreFamilyMember(member)}
                />
              ) : null}

              {panel === 'education' ? (
                <EducationPanel
                  record={record}
                  onAdd={() => setEducationEditing({ education: null })}
                  onEdit={(education) => setEducationEditing({ education })}
                  onDelete={(education) => setEducationDeleting(education)}
                  onBulkDelete={(educations) => setEducationBulkDeleting(educations)}
                  onRestore={(education) => void restoreEducation(education)}
                />
              ) : null}

              {panel === 'eligibility' ? (
                <EligibilityPanel
                  record={record}
                  onAdd={() => setEligibilityEditing({ row: null })}
                  onEdit={(row) => setEligibilityEditing({ row })}
                  onDelete={(row) => setEligibilityDeleting(row)}
                  onBulkDelete={(rows) => setEligibilityBulkDeleting(rows)}
                  onRestore={(row) => void restoreEligibility(row)}
                />
              ) : null}

              {panel === 'work-experience' ? (
                <WorkExperiencePanel
                  record={record}
                  onAdd={() => setWorkExperienceEditing({ row: null })}
                  onEdit={(row) => setWorkExperienceEditing({ row })}
                  onDelete={(row) => setWorkExperienceDeleting(row)}
                  onBulkDelete={(rows) => setWorkExperienceBulkDeleting(rows)}
                  onRestore={(row) => void restoreWorkExperience(row)}
                />
              ) : null}

              {panel === 'voluntary-work' ? (
                <VoluntaryWorkPanel
                  record={record}
                  onAdd={() => setVoluntaryWorkEditing({ row: null })}
                  onEdit={(row) => setVoluntaryWorkEditing({ row })}
                  onDelete={(row) => setVoluntaryWorkDeleting(row)}
                  onBulkDelete={(rows) => setVoluntaryWorkBulkDeleting(rows)}
                  onRestore={(row) => void restoreVoluntaryWork(row)}
                />
              ) : null}

              {panel === 'training' ? (
                <TrainingPanel
                  record={record}
                  onAdd={() => setTrainingEditing({ row: null })}
                  onEdit={(row) => setTrainingEditing({ row })}
                  onDelete={(row) => setTrainingDeleting(row)}
                  onBulkDelete={(rows) => setTrainingBulkDeleting(rows)}
                  onRestore={(row) => void restoreTraining(row)}
                />
              ) : null}

              {panel === 'special-skills' ? (
                <SpecialSkillsPanel
                  record={record}
                  onAdd={() => setSpecialSkillEditing({ row: null })}
                  onEdit={(row) => setSpecialSkillEditing({ row })}
                  onDelete={(row) => setSpecialSkillDeleting(row)}
                  onBulkDelete={(rows) => setSpecialSkillBulkDeleting(rows)}
                  onRestore={(row) => void restoreSpecialSkill(row)}
                />
              ) : null}

              {panel === 'recognition' ? (
                <RecognitionPanel
                  record={record}
                  onAdd={() => setRecognitionEditing({ row: null })}
                  onEdit={(row) => setRecognitionEditing({ row })}
                  onDelete={(row) => setRecognitionDeleting(row)}
                  onBulkDelete={(rows) => setRecognitionBulkDeleting(rows)}
                  onRestore={(row) => void restoreRecognition(row)}
                />
              ) : null}

              {panel === 'references' ? (
                <ReferencesPanel
                  record={record}
                  onAdd={() => setReferenceEditing({ row: null })}
                  onEdit={(row) => setReferenceEditing({ row })}
                  onDelete={(row) => setReferenceDeleting(row)}
                  onBulkDelete={(rows) => setReferenceBulkDeleting(rows)}
                  onRestore={(row) => void restoreReference(row)}
                />
              ) : null}

              {panel === 'disciplinary' ? (
                <DisciplinaryPanel
                  record={record}
                  onAdd={() => setDisciplinaryEditing({ row: null })}
                  onEdit={(row) => setDisciplinaryEditing({ row })}
                  onDelete={(row) => setDisciplinaryDeleting(row)}
                  onBulkDelete={(rows) => setDisciplinaryBulkDeleting(rows)}
                  onRestore={(row) => void restoreDisciplinaryRecord(row)}
                />
              ) : null}

              {panel === 'saln' ? (
                <SalnPanel
                  record={record}
                  onAdd={() => setSalnEditing({ row: null })}
                  onEdit={(row) => setSalnEditing({ row })}
                  onDelete={(row) => setSalnDeleting(row)}
                  onBulkDelete={(rows) => setSalnBulkDeleting(rows)}
                  onRestore={(row) => void restoreSaln(row)}
                />
              ) : null}

              {panel === 'survey' ? (
                <SurveyPanel
                  record={record}
                  onUpdate={(question, answer) => setSurveyEditing({ question, answer })}
                />
              ) : null}
            </>
          )}
        </ProfileShell>
      </div>

      {/*
        One dedicated editor per section, each over its own fields. They all save through
        `PUT /api/personnel/{id}` and send the sections the reader did not open back unchanged
        — and none of them sends the identity block, so correcting an address needs
        `personnel.update` and not `personnel.reveal`.

        `record &&` guards every one: the page renders a skeleton before the record lands, and
        a modal cannot seed itself from a record that is not there yet.
      */}
      {record ? (
        <>
          {/*
            The one editor here that does NOT save through `PUT /api/personnel/{id}` — a
            photograph is written to the identity store, keyed by `PersonId`. So it reloads
            the photograph rather than the record: nothing in `rsys_` changed.
          */}
          <EditPhotoModal
            open={editing === 'photo'}
            personId={record.personId}
            currentUrl={photo.url}
            canRemove={hasPermission('personnel.delete')}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              photo.reload();
            }}
          />

          {/*
            ISSUED ASSETS — Logistics' own two forms, reused rather than re-drawn. Mounted only
            while open, because both seed their state on mount. A save refetches the issued
            assets alone: nothing else on this page reads an issuance.
          */}
          {issuingAsset ? (
            <IssuanceModal
              open
              initialAccountablePersonnelId={record.id}
              onClose={() => setIssuingAsset(false)}
              onSaved={() => {
                setIssuingAsset(false);
                setIssuedAssetsReloadKey((k) => k + 1);
                toast.success('Stock issued.');
              }}
            />
          ) : null}

          {returningAsset ? (
            <RecordReturnModal
              open
              issuance={returningAsset.issuance}
              initialLineId={returningAsset.lineId}
              onClose={() => setReturningAsset(null)}
              onSaved={() => {
                setReturningAsset(null);
                setIssuedAssetsReloadKey((k) => k + 1);
                toast.success('Return recorded.');
              }}
            />
          ) : null}

          {/* The LINK, which is this record's own column. The account itself is the users
              module's, and the card links out to it. */}
          <EditAccountLinkModal
            open={editing === 'account'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditPersonalModal
            open={editing === 'personal'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditAddressModal
            open={editing === 'address'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditIdentificationModal
            open={editing === 'identification'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditMilitaryModal
            open={editing === 'military'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          {/*
            Its own key, so the modal mounts only for a reader entitled to set a level — and
            the card's cog is gated on the same key, so the two cannot disagree about who may
            open it.
          */}
          <EditAccessLevelModal
            record={editing === 'access-level' ? record : null}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditHmoModal
            open={editing === 'hmo'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />

          <EditEmploymentModal
            open={editing === 'employment'}
            record={record}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />
        </>
      ) : null}

      {/*
        This record's audit trail, not the module's — `entityId` is what scopes it, and the
        server reads it against the row's parent as well as its own entity. That second half
        is what makes a SECTION's write visible here: a 201 file's sections are child tables,
        so recording a survey answer stamps the ANSWER's id, and filtering on this record's id
        alone showed one event out of three while the two survey writes sat in `AuditLogs`
        unreachable.

        No `entityTypes` beside it, deliberately — scope by id or by type, never both. A write
        the server REFUSED never reached an entity, so it carries no type and a type filter
        would drop exactly the event a reader comes here to find.

        The photo writes are still the one thing it will not show: those go through
        `/api/persons/{personId}/photo`, so both their entity and their parent are the identity
        store's `PersonId` rather than this record's id.
      */}
      {record ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="personnel"
          entityId={record.id}
        />
      ) : null}

      {/* Writes nothing, so no confirm tick — see `<ImagePreviewModal>`. */}
      <ImagePreviewModal
        id="personnel-photo-preview"
        open={previewingPhoto}
        url={photo.url}
        title="Photograph"
        alt={`Photograph of ${displayName}`}
        onClose={() => setPreviewingPhoto(false)}
      />

      {recordingService && record ? (
        <ServiceRecordModal
          personnelId={record.id}
          hasOpenServiceRecord={openServiceRecords.length > 0}
          onClose={() => setRecordingService(false)}
          onSaved={() => {
            setRecordingService(false);
            refresh();
          }}
        />
      ) : null}

      {ending && record ? (
        <EndServiceRecordModal
          personnelId={record.id}
          serviceRecord={ending}
          onClose={() => setEnding(null)}
          onSaved={() => {
            setEnding(null);
            refresh();
          }}
        />
      ) : null}

      {familyEditing && record ? (
        <EditFamilyMemberModal
          personnelId={record.id}
          editing={familyEditing.member}
          onClose={() => setFamilyEditing(null)}
          onSaved={() => {
            setFamilyEditing(null);
            refresh();
          }}
        />
      ) : null}

      {familyDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-family-delete"
          title="Delete this family member?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              This member will be hidden from the record. They are kept and can be restored from
              Show deleted, and their name is left untouched in the identity store.
            </p>
          }
          onClose={() => setFamilyDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteFamilyMember(record.id, familyDeleting.id);
            setFamilyDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {/*
        The one bulk dialog that names nothing it is deleting. A family member’s name lives in
        `rsys_pii`, and the PANEL resolves it for the rows on screen; this page holds `PersonId`s
        and no names, so the count is the honest thing to state. The single delete above says
        nothing either, for the same reason.
      */}
      {familyBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-family-bulk-delete"
          title={`Delete ${familyBulkDeleting.length} family member${
            familyBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {familyBulkDeleting.length === 1 ? 'This member' : 'These members'} will be hidden
                from this record.
              </p>
              <p>
                They are kept and can be restored one at a time from Show deleted, and their names
                are left untouched in the identity store.
              </p>
            </div>
          }
          onClose={() => setFamilyBulkDeleting(null)}
          onConfirm={async () => {
            /* One call per row — there is no bulk endpoint, and there should not be. */
            for (const row of familyBulkDeleting) {
              await personnelApi.deleteFamilyMember(record.id, row.id);
            }

            setFamilyBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {eligibilityEditing && record ? (
        <EditEligibilityModal
          personnelId={record.id}
          editing={eligibilityEditing.row}
          onClose={() => setEligibilityEditing(null)}
          onSaved={() => {
            setEligibilityEditing(null);
            refresh();
          }}
        />
      ) : null}

      {eligibilityDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-eligibility-delete"
          title="Delete this eligibility record?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">
                {eligibilityDeleting.eligibilityName}
              </span>{' '}
              will be hidden from this record. It is kept and can be restored from Show deleted.
            </p>
          }
          onClose={() => setEligibilityDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteEligibility(record.id, eligibilityDeleting.id);
            setEligibilityDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {eligibilityBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-eligibility-bulk-delete"
          title={`Delete ${eligibilityBulkDeleting.length} eligibility record${
            eligibilityBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {eligibilityBulkDeleting.length === 1 ? 'This record' : 'These records'} will be
                hidden from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {eligibilityBulkDeleting.map((x) => x.eligibilityName).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setEligibilityBulkDeleting(null)}
          onConfirm={async () => {
            /* One call per row — there is no bulk endpoint, and there should not be. */
            for (const row of eligibilityBulkDeleting) {
              await personnelApi.deleteEligibility(record.id, row.id);
            }

            setEligibilityBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {surveyEditing && record ? (
        <EditSurveyAnswerModal
          personnelId={record.id}
          question={surveyEditing.question}
          editing={surveyEditing.answer}
          onClose={() => setSurveyEditing(null)}
          onSaved={() => {
            setSurveyEditing(null);
            refresh();
          }}
        />
      ) : null}

      {workExperienceEditing && record ? (
        <EditWorkExperienceModal
          personnelId={record.id}
          editing={workExperienceEditing.row}
          onClose={() => setWorkExperienceEditing(null)}
          onSaved={() => {
            setWorkExperienceEditing(null);
            refresh();
          }}
        />
      ) : null}

      {workExperienceDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-work-experience-delete"
          title="Delete this work experience record?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">
                {workExperienceDeleting.positionTitle}
              </span>{' '}
              at{' '}
              <span className="font-medium text-slate-800">
                {workExperienceDeleting.agencyName}
              </span>{' '}
              will be hidden from this record. It is kept and can be restored from Show deleted.
            </p>
          }
          onClose={() => setWorkExperienceDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteWorkExperience(record.id, workExperienceDeleting.id);
            setWorkExperienceDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {workExperienceBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-work-experience-bulk-delete"
          title={`Delete ${workExperienceBulkDeleting.length} work experience record${
            workExperienceBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {workExperienceBulkDeleting.length === 1 ? 'This record' : 'These records'} will
                be hidden from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {workExperienceBulkDeleting.map((x) => x.positionTitle).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setWorkExperienceBulkDeleting(null)}
          onConfirm={async () => {
            /* One call per row — there is no bulk endpoint, and there should not be. */
            for (const row of workExperienceBulkDeleting) {
              await personnelApi.deleteWorkExperience(record.id, row.id);
            }

            setWorkExperienceBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {voluntaryWorkEditing && record ? (
        <EditVoluntaryWorkModal
          personnelId={record.id}
          editing={voluntaryWorkEditing.row}
          onClose={() => setVoluntaryWorkEditing(null)}
          onSaved={() => {
            setVoluntaryWorkEditing(null);
            refresh();
          }}
        />
      ) : null}

      {voluntaryWorkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-voluntary-work-delete"
          title="Delete this voluntary work record?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">
                {voluntaryWorkDeleting.positionOrNature}
              </span>{' '}
              at{' '}
              <span className="font-medium text-slate-800">
                {voluntaryWorkDeleting.organizationName}
              </span>{' '}
              will be hidden from this record. It is kept and can be restored from Show deleted.
            </p>
          }
          onClose={() => setVoluntaryWorkDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteVoluntaryWork(record.id, voluntaryWorkDeleting.id);
            setVoluntaryWorkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {voluntaryWorkBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-voluntary-work-bulk-delete"
          title={`Delete ${voluntaryWorkBulkDeleting.length} record${
            voluntaryWorkBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {voluntaryWorkBulkDeleting.length === 1 ? 'This record' : 'These records'} will be
                hidden from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {voluntaryWorkBulkDeleting.map((x) => x.organizationName).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setVoluntaryWorkBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request
              soft-deleting a list would be a mass deletion wearing a selection's clothes —
              which is what `clear-data` is, with its own key.
            */
            for (const row of voluntaryWorkBulkDeleting) {
              await personnelApi.deleteVoluntaryWork(record.id, row.id);
            }

            setVoluntaryWorkBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {trainingEditing && record ? (
        <EditTrainingModal
          personnelId={record.id}
          editing={trainingEditing.row}
          onClose={() => setTrainingEditing(null)}
          onSaved={() => {
            setTrainingEditing(null);
            refresh();
          }}
        />
      ) : null}

      {trainingDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-training-delete"
          title="Delete this L&D record?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">{trainingDeleting.title}</span>{' '}
              <span className="font-mono text-slate-800">({trainingDeleting.recordNumber})</span>{' '}
              will be hidden from this record. It is kept and can be restored from Show deleted,
              with the number it always had.
            </p>
          }
          onClose={() => setTrainingDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteTraining(record.id, trainingDeleting.id);
            setTrainingDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {trainingBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-training-bulk-delete"
          title={`Delete ${trainingBulkDeleting.length} L&D record${
            trainingBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {trainingBulkDeleting.length === 1 ? 'This record' : 'These records'} will be
                hidden from this profile —{' '}
                <span className="font-mono text-slate-800">
                  {trainingBulkDeleting.map((x) => x.recordNumber).join(', ')}
                </span>
                .
              </p>
              <p>
                They are kept and can be restored one at a time from Show deleted, each with the
                number it always had.
              </p>
            </div>
          }
          onClose={() => setTrainingBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request
              soft-deleting a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of trainingBulkDeleting) {
              await personnelApi.deleteTraining(record.id, row.id);
            }

            setTrainingBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {specialSkillEditing && record ? (
        <EditSpecialSkillModal
          personnelId={record.id}
          editing={specialSkillEditing.row}
          onClose={() => setSpecialSkillEditing(null)}
          onSaved={() => {
            setSpecialSkillEditing(null);
            refresh();
          }}
        />
      ) : null}

      {specialSkillDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-special-skill-delete"
          title="Delete this special skill?"
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                <span className="font-medium text-slate-800">{specialSkillDeleting.name}</span>{' '}
                will be hidden from this profile.
              </p>
              <p>
                It is kept and can be restored from Show deleted, unless the skill has been
                recorded again by then.
              </p>
            </div>
          }
          onClose={() => setSpecialSkillDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteSpecialSkill(record.id, specialSkillDeleting.id);
            setSpecialSkillDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {specialSkillBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-special-skill-bulk-delete"
          title={`Delete ${specialSkillBulkDeleting.length} skill${
            specialSkillBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {specialSkillBulkDeleting.length === 1 ? 'This skill' : 'These skills'} will be
                hidden from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {specialSkillBulkDeleting.map((x) => x.name).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setSpecialSkillBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request soft-deleting
              a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of specialSkillBulkDeleting) {
              await personnelApi.deleteSpecialSkill(record.id, row.id);
            }

            setSpecialSkillBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {disciplinaryEditing && record ? (
        <EditDisciplinaryModal
          personnelId={record.id}
          editing={disciplinaryEditing.row}
          onClose={() => setDisciplinaryEditing(null)}
          onSaved={() => {
            setDisciplinaryEditing(null);
            refresh();
          }}
        />
      ) : null}

      {disciplinaryDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-disciplinary-delete"
          title="Delete this record?"
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>This record will be hidden from this profile.</p>
              <p>
                {/*
                  THE DISTINCTION THIS DIALOG EXISTS TO MAKE. Deleting is a correction to the
                  FILE — the record was entered against the wrong employee, or twice. That the
                  charge did not stand is the case status Dismissed, which stays on the record
                  and stays visible, because it is a fact the employee has every interest in
                  the file keeping.
                */}
                It is kept and can be restored from Show deleted. <strong>This is not the same
                as the case being dismissed</strong> — if the charge did not stand, set the case
                status to Dismissed instead, so the record says so.
              </p>
            </div>
          }
          onClose={() => setDisciplinaryDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteDisciplinaryRecord(record.id, disciplinaryDeleting.id);
            setDisciplinaryDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {disciplinaryBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-disciplinary-bulk-delete"
          title={`Delete ${disciplinaryBulkDeleting.length} record${
            disciplinaryBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {disciplinaryBulkDeleting.length === 1 ? 'This record' : 'These records'} will be
                hidden from this profile.
              </p>
              <p>
                They are kept and can be restored one at a time from Show deleted. This is not
                the same as the cases being dismissed.
              </p>
            </div>
          }
          onClose={() => setDisciplinaryBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request soft-deleting
              a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of disciplinaryBulkDeleting) {
              await personnelApi.deleteDisciplinaryRecord(record.id, row.id);
            }

            setDisciplinaryBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {salnEditing && record ? (
        <EditSalnModal
          personnelId={record.id}
          editing={salnEditing.row}
          onClose={() => setSalnEditing(null)}
          onSaved={() => {
            setSalnEditing(null);
            refresh();
          }}
        />
      ) : null}

      {salnDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-saln-delete"
          title={`Delete the ${salnDeleting.asOfYear} SALN filing?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>This filing will be hidden from this profile.</p>
              <p>
                {/*
                  SAYS WHAT STAYS, which is the sentence that makes one honest word possible:
                  "delete" here never means erased. The schedules are specifically named because
                  a reader who has just typed four tables of them needs to know they are not
                  being thrown away with the filing.
                */}
                It is kept and can be restored from Show deleted, with every declared property,
                liability and connection exactly as they are now.
              </p>
            </div>
          }
          onClose={() => setSalnDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteSaln(record.id, salnDeleting.id);
            setSalnDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {salnBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-saln-bulk-delete"
          title={`Delete ${salnBulkDeleting.length} filing${
            salnBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {salnBulkDeleting.length === 1 ? 'This filing' : 'These filings'} will be hidden
                from this profile.
              </p>
              <p>
                They are kept and can be restored one at a time from Show deleted, with their
                schedules untouched.
              </p>
            </div>
          }
          onClose={() => setSalnBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request soft-deleting
              a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of salnBulkDeleting) {
              await personnelApi.deleteSaln(record.id, row.id);
            }

            setSalnBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {referenceEditing && record ? (
        <EditReferenceModal
          personnelId={record.id}
          editing={referenceEditing.row}
          onClose={() => setReferenceEditing(null)}
          onSaved={() => {
            setReferenceEditing(null);
            refresh();
          }}
        />
      ) : null}

      {referenceDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-reference-delete"
          title="Delete this reference?"
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              {/*
                NAMED BY WHAT THIS PAGE HOLDS, which is not the name — that is in the other
                database and this dialog has not asked for it. Saying "this reference" is honest;
                fetching a name to put in a confirmation would be a reveal nobody asked for, on a
                screen whose whole job is to hide the row.
              */}
              <p>This reference will be hidden from this profile.</p>
              <p>
                It is kept and can be restored from Show deleted. The person&rsquo;s name is left
                untouched in the identity store — removing a referee from a 201 file is a
                correction to that file, not a statement that the person never existed.
              </p>
            </div>
          }
          onClose={() => setReferenceDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteReference(record.id, referenceDeleting.id);
            setReferenceDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {referenceBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-reference-bulk-delete"
          title={`Delete ${referenceBulkDeleting.length} reference${
            referenceBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {referenceBulkDeleting.length === 1 ? 'This reference' : 'These references'} will
                be hidden from this profile.
              </p>
              <p>
                They are kept and can be restored one at a time from Show deleted, and their names
                are left untouched in the identity store.
              </p>
            </div>
          }
          onClose={() => setReferenceBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request soft-deleting
              a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of referenceBulkDeleting) {
              await personnelApi.deleteReference(record.id, row.id);
            }

            setReferenceBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {recognitionEditing && record ? (
        <EditRecognitionModal
          personnelId={record.id}
          editing={recognitionEditing.row}
          onClose={() => setRecognitionEditing(null)}
          onSaved={() => {
            setRecognitionEditing(null);
            refresh();
          }}
        />
      ) : null}

      {recognitionDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-recognition-delete"
          title="Delete this recognition?"
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                <span className="font-medium text-slate-800">{recognitionDeleting.title}</span>{' '}
                will be hidden from this profile.
              </p>
              {/*
                No "unless it has been recorded again by then" caveat, which the skill's own
                dialog carries: nothing here is unique, so a restore has nothing to collide with.
              */}
              <p>It is kept and can be restored from Show deleted.</p>
            </div>
          }
          onClose={() => setRecognitionDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteRecognition(record.id, recognitionDeleting.id);
            setRecognitionDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {recognitionBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-recognition-bulk-delete"
          title={`Delete ${recognitionBulkDeleting.length} recognition${
            recognitionBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {recognitionBulkDeleting.length === 1 ? 'This recognition' : 'These recognitions'}{' '}
                will be hidden from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {recognitionBulkDeleting.map((x) => x.title).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setRecognitionBulkDeleting(null)}
          onConfirm={async () => {
            /*
              One call per row, in sequence. There is no bulk endpoint and there should not be:
              each delete is an audited write against one row, and a single request soft-deleting
              a list would be a mass deletion wearing a selection's clothes.
            */
            for (const row of recognitionBulkDeleting) {
              await personnelApi.deleteRecognition(record.id, row.id);
            }

            setRecognitionBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {educationEditing && record ? (
        <EditEducationModal
          personnelId={record.id}
          editing={educationEditing.education}
          onClose={() => setEducationEditing(null)}
          onSaved={() => {
            setEducationEditing(null);
            refresh();
          }}
        />
      ) : null}

      {/*
        Delete asks, restore does not. A soft delete is reversible and the copy says so —
        which is what makes one honest word possible where a gentler synonym was the
        temptation.

        No password: `requireCredential` is for what DESTROYS or REVOKES. This hides a line
        that Show deleted brings straight back, so the tick is the whole ceremony it earns.
      */}
      {educationDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-education-delete"
          title="Delete this education line?"
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">{educationDeleting.schoolName}</span>{' '}
              will be hidden from this record. It is kept and can be restored from Show deleted.
            </p>
          }
          onClose={() => setEducationDeleting(null)}
          onConfirm={async () => {
            await personnelApi.deleteEducation(record.id, educationDeleting.id);
            setEducationDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {educationBulkDeleting && record ? (
        <ConfirmDialog
          open
          id="personnel-education-bulk-delete"
          title={`Delete ${educationBulkDeleting.length} education line${
            educationBulkDeleting.length === 1 ? '' : 's'
          }?`}
          danger
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <p>
                {educationBulkDeleting.length === 1 ? 'This line' : 'These lines'} will be hidden
                from this profile —{' '}
                <span className="font-medium text-slate-800">
                  {educationBulkDeleting.map((e) => e.schoolName).join(', ')}
                </span>
                .
              </p>
              <p>They are kept and can be restored one at a time from Show deleted.</p>
            </div>
          }
          onClose={() => setEducationBulkDeleting(null)}
          onConfirm={async () => {
            /* One call per row — there is no bulk endpoint, and there should not be. */
            for (const row of educationBulkDeleting) {
              await personnelApi.deleteEducation(record.id, row.id);
            }

            setEducationBulkDeleting(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * What stands in for a panel while its content is being fetched.
 *
 * Three cards' worth, because every panel here is a stack of `<ProfileCard>`s and a skeleton
 * that is the wrong shape reads as a layout that has broken rather than one that is loading.
 */
function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} className="h-40 w-full" />
      ))}
    </div>
  );
}

/**
 * The first panel, and it holds the audit trail.
 *
 * **It used to hold the record's basic facts as well**, which is what every other profile
 * here still does. Product call, 14 Sep 2026: a personnel record carries enough basic
 * information to be a panel of its own, so it became one — `Basic info`, immediately after
 * this.
 *
 * **The reader still starts here.** Opening on `Basic info` was tried on 14 Sep and reversed
 * on 15 Sep: that panel sits inside the `201 File` group, which starts closed, so the page
 * landed inside a collapsed group and the rail arrived unable to say what sections the record
 * has. `Info` is outside the group, so the split moved what is IN this panel and not where
 * the reader begins.
 */
function InfoPanel({ record, onEditAccessLevel }: {
  record: PersonnelDetail;
  onEditAccessLevel: () => void;
}) {
  return (
    <ProfileSection>
      <div className="flex flex-col gap-5">
        {/*
          WHO MAY OPEN THIS FILE. On Info rather than inside the 201 group, because it is a fact
          about the RECORD rather than a section of the document — the same reading that puts
          provenance here.

          Its own card and its own Edit, because the write is its own key: `personnel.update`
          corrects what the record says, and `personnel.set-access-level` decides who may read
          it at all. A reader without that key sees the card and no cog, which is correct — the
          classification is worth stating to everyone who can open the record.
        */}
        <ProfileCard
          id="personnel-access-level"
          title="Access level"
          onEdit={onEditAccessLevel}
          editPermission="personnel.set-access-level"
          empty={
            record.accessLevelId == null
              ? {
                  title: 'This record is unclassified',
                  description:
                    'Everyone who may read personnel records can open it. Classifying it limits that to readers tagged at the level you choose, or stronger.',
                }
              : undefined
          }
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Level', value: record.accessLevelName },
              {
                label: 'Rank',
                value: record.accessLevelRank != null ? String(record.accessLevelRank) : null,
                mono: true,
              },
              {
                /*
                  The comparison spelled out, because the number states the opposite of what a
                  reader assumes: a HIGHER rank is a WEAKER clearance. It is the rule the server
                  applies, written in the words the screen uses, and it is what tells a reader
                  why a colleague cannot see this file.
                */
                label: 'Read by',
                value:
                  record.accessLevelRank != null
                    ? `Readers tagged ${record.accessLevelName} or stronger. Everyone else sees this record listed and counted, with the name withheld.`
                    : null,
              },
            ]}
          />
        </ProfileCard>

        {/*
          Audit is never editable — no cog, at any permission level.

          It carries the WHOLE trail. The header states one line of it — the latest change, or
          the creation when there has been none — and that line is a summary rather than the
          record: which half it happens to be showing depends on the row, so a card holding
          "the other half" would be a different shape per record and the reader could not tell
          a missing fact from one that does not apply.
        */}
        <ProfileCard id="personnel-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Created',
                value: formatDateTime(record.createdAt),
                mono: true,
              },
              { label: 'Created by', value: record.createdByName },
              {
                label: 'Last updated',
                value: record.updatedAt ? formatDateTime(record.updatedAt) : null,
                mono: true,
              },
              { label: 'Updated by', value: record.updatedByName },
            ]}
          />
        </ProfileCard>
      </div>
    </ProfileSection>
  );
}

/**
 * A date of birth reads as an age, so the row carries both.
 *
 * The date is the value and the age is a gloss on it — muted, in parentheses — because the
 * record stores one of them and the other is arithmetic. `calendarAge` does that arithmetic
 * in one place: subtracting years from a timestamp averages leap years and drifts a day
 * either side of a birthday, and putting a calendar date through the instant path tells a
 * reader west of UTC the day before the one on the record.
 */
function withAge(dateOfBirth: string | null | undefined): ReactNode {
  if (!dateOfBirth) return null;

  const age = calendarAge(dateOfBirth);

  return (
    <>
      {formatDateOnly(dateOfBirth)}
      {age === null ? null : (
        <span className="text-slate-400">
          {' '}
          ({age} {age === 1 ? 'yr' : 'yrs'})
        </span>
      )}
    </>
  );
}

/**
 * Everything the create form's Personal step collects — the name through to the disability
 * flag — and the erasure notice, which belongs beside the name it is about.
 *
 * Split out of `Info` on 14 Sep 2026. One card, one Edit — the name and the date of birth
 * are in it even though they are written to another database.
 *
 * They are read when this panel mounts, for a reader who holds `personnel.reveal`, so
 * opening a profile DOES write an access row for them. That is the trade, taken on
 * 15 Sep 2026 and argued at the state below.
 */
function BasicInfoPanel({
  record,
  erased,
  onEdit,
  onEditMilitary,
}: {
  record: PersonnelDetail;
  erased: boolean;
  onEdit: () => void;
  /** The Military card's own Edit — its own section, so its own modal. */
  onEditMilitary: () => void;
}) {
  const { hasPermission } = useAuth();

  const canReveal = hasPermission('personnel.reveal');

  /*
    The name and the date of birth, from the identity store.

    **Fetched when the panel opens, for a reader who holds the key.** It sat behind a cog
    entry somebody had to click, on the argument that a page which revealed in order to
    draw itself would audit an access for everyone who merely opened the record.

    **Product call, 15 Sep 2026, reversed.** The header three inches above already prints
    the whole display name, resolved under `personnel.read` — so the card was saying
    "Hidden" about a value that was already on screen, which is one fact stated two ways on
    one page. And the trail comes out more honest rather than less: a reveal row now records
    a page that genuinely showed the name and the date of birth, where before it recorded a
    click.

    The cost is stated rather than buried: every profile a reveal-holder opens writes one
    access row. A reader WITHOUT the key still reads "Hidden" and no request is made.

    Held in the panel rather than the page because it is this card's business, and it goes
    when the reader leaves.
  */
  const [identity, setIdentity] = useState<PersonIdentity | null>(null);
  const [failed, setFailed] = useState(false);

  /*
    A retry counter, so the entry below can re-run the effect. Nothing reads it — bumping it
    IS the request, which is what keeps one fetch path here instead of two.
  */
  const [attempt, setAttempt] = useState(0);

  /*
    An ERASED record has nothing to reveal — the store answers one with a 404, and the
    banner above already says why — so no request is made for it and those rows fall to an
    em dash, which is the true statement: the record has no value for them any more.
  */
  const revealable = canReveal && !erased;

  useEffect(() => {
    if (!revealable) return;

    const controller = new AbortController();

    setIdentity(null);
    setFailed(false);

    personsApi
      .reveal(record.personId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setIdentity(next);
      })
      .catch(() => {
        // Nothing else on the card depends on these five rows, so a failure here must not
        // take the panel down with it: they say so, and the rest of the card still reads.
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => controller.abort();
  }, [revealable, record.personId, attempt]);

  /*
    Three absences that look alike and are not, exactly as a resolved name has four.

    Hidden is NOT an em dash. An em dash means the record has no value for this field, and
    every employee has a name — what is true for a reader without the key is that this page
    has not been given it. The row exists either way, so they can see the fact is held
    rather than missing.
  */
  const guarded: ReactNode = (
    <span className="text-slate-400">
      {failed ? 'Unavailable' : revealable ? '…' : 'Hidden'}
    </span>
  );

  /*
    One place deciding what a guarded row shows, so the five of them cannot disagree about
    an absence. `null` is what draws <PreviewTable>'s em dash.
  */
  function identityValue(of: (found: PersonIdentity) => ReactNode): ReactNode {
    if (identity) return of(identity);
    if (erased) return null;

    return guarded;
  }

  return (
    <ProfileSection>
      <div className="flex flex-col gap-5">
        {erased ? (
          <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">
              This person&apos;s identity was erased. The employment record is kept, and the name
              and date of birth cannot be recovered.
            </p>
          </div>
        ) : null}

        {/*
          What the Personal step collects. Employment moved to its own panel.

          ONE Edit, which is the rule: a section gets one. The name and the date of birth are
          inside it even though they are written to a different database — the editor drops
          them for a reader who cannot reveal them, rather than the card growing a second
          entry that makes our storage layout the reader's problem.
        */}
        <ProfileCard
          id="personnel-personal"
          title="Personal"
          onEdit={onEdit}
          editPermission="personnel.update"
          /*
            A second entry, and it is not a second Edit — it reads rather than writes, which
            is the test that rule sets. It carries an icon because the Edit above it does:
            one menu, one treatment.

            It appears only once the automatic reveal has FAILED. On the happy path the
            values are already up, so an entry offering to fetch them would do nothing; and
            it is gone for a reader without the key, so nobody is offered an access they
            cannot make. A failure with no way back is the dead end this exists to prevent.
          */
          menuItems={
            failed && revealable
              ? [
                  {
                    key: 'reveal',
                    label: 'Reveal',
                    icon: <Eye className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setAttempt((n) => n + 1),
                  },
                ]
              : []
          }
        >
          <PreviewTable
            variant="table-display"
            rows={[
              /*
                The name first, as the form that collects it does. These five come from the
                identity store rather than from the record on this page — see the reveal
                above for when they arrive and what stands in until they do.
              */
              { label: 'First name', value: identityValue((i) => i.firstName) },
              { label: 'Middle name', value: identityValue((i) => i.middleName ?? null) },
              { label: 'Last name', value: identityValue((i) => i.lastName) },
              { label: 'Suffix', value: identityValue((i) => i.suffix ?? null) },
              {
                label: 'Date of birth',
                /*
                  A CALENDAR date, so the conversion-free formatter: the instant path would
                  tell a reader west of UTC the day before the one on the record.
                */
                value: identityValue((i) => withAge(i.dateOfBirth)),
                mono: true,
              },
              { label: 'Sex', value: SEX_LABELS[record.sex] },
              {
                label: 'Civil status',
                value: record.civilStatus ? CIVIL_STATUS_LABELS[record.civilStatus] : null,
              },
              { label: 'Blood type', value: record.bloodTypeName },
              { label: 'Nationality', value: record.nationalityName },
              {
                label: 'Citizenship',
                value: record.citizenship ? CITIZENSHIP_LABELS[record.citizenship] : null,
              },
              /*
                Both rows render whatever the value, like every other row in this table — a
                record that is not a dual citizen shows two em dashes rather than dropping the
                rows, or the card would be a different shape per person and a reader could not
                tell a missing fact from one that does not apply.
              */
              {
                label: 'Dual citizenship',
                value: record.dualCitizenshipKind
                  ? DUAL_CITIZENSHIP_KIND_LABELS[record.dualCitizenshipKind]
                  : null,
              },
              { label: 'Country', value: record.dualCitizenshipCountryName ?? null },
              { label: 'Religion', value: record.religionName },
              {
                label: 'Contact number',
                value: record.contactNumber ? (
                  <span className="font-mono">{record.contactNumber}</span>
                ) : null,
              },
              {
                label: 'Email',
                value: record.email ? <span className="font-mono">{record.email}</span> : null,
              },
              {
                label: 'Person with disability',
                value: record.isPwd ? (record.pwdIdNumber ?? 'Yes') : null,
              },
            ]}
          />
        </ProfileCard>

        {/*
          Drawn only for a record that claims service — the conditionality the nav entry used
          to carry, moved to where it costs nothing. A record that never served has no military
          block to show, and a card of em dashes would tell the reader nothing the flag had not
          already said.
        */}
        {record.isMilitary ? <MilitaryCard record={record} onEdit={onEditMilitary} /> : null}
      </div>
    </ProfileSection>
  );
}

/**
 * Where this person lives — present, and permanent when it differs.
 *
 * The parts are the text that was chosen, not a join through the address catalog: an address
 * records what someone stated at a point in time, so correcting a city's name in the catalog
 * does not rewrite the addresses already collected.
 */
function AddressPanel({ record, onEdit }: { record: PersonnelDetail; onEdit: () => void }) {
  const present = record.addresses.find((a) => a.kind === ADDRESS_KIND.present) ?? null;
  const permanent = record.addresses.find((a) => a.kind === ADDRESS_KIND.permanent) ?? null;

  const rows = (address: PersonnelAddress | null) => [
    { label: 'Street', value: address?.streetAddress ?? null },
    { label: 'Barangay', value: address?.barangay ?? null },
    { label: 'City / municipality', value: address?.cityMunicipality ?? null },
    { label: 'Province', value: address?.province ?? null },
    { label: 'Postal code', value: address?.zipCode ?? null },
  ];

  return (
    <ProfileSection>
      <div className="flex flex-col gap-5">
        <ProfileCard
          id="personnel-address-present"
          title="Present address"
          onEdit={onEdit}
          editPermission="personnel.update"
        >
          <PreviewTable variant="table-display" rows={rows(present)} />
        </ProfileCard>

        <ProfileCard
          id="personnel-address-permanent"
          title="Permanent address"
          /*
            Both address cards open the SAME editor, and deliberately: "same as present" is one
            flag spanning the two, so splitting them would let a reader assert it on one card
            while editing a separate permanent address on the other.
          */
          onEdit={onEdit}
          editPermission="personnel.update"
        >
          {record.permanentAddressSameAsPresent ? (
            /*
              The flag is a statement, not an absence: without it, "no permanent row" is
              ambiguous between "the same as present" and "never collected", and those are
              different facts on a form that asks for both.
            */
            <p className="px-4 py-3 text-sm text-slate-600">Same as the present address.</p>
          ) : (
            <PreviewTable variant="table-display" rows={rows(permanent)} />
          )}
        </ProfileCard>
      </div>
    </ProfileSection>
  );
}

/**
 * The identification numbers this employee holds.
 *
 * One row per number actually recorded, not per type in the catalog: a record that holds two
 * numbers should read as two, and the empty state says where the fields come from.
 */
function IdentificationPanel({ record, onEdit }: { record: PersonnelDetail; onEdit: () => void }) {
  return (
    <ProfileSection>
      <ProfileCard
        id="personnel-identification"
        title="Numbers held"
        onEdit={onEdit}
        editPermission="personnel.update"
        empty={
          record.identifications.length > 0
            ? undefined
            : {
                title: 'No identification numbers recorded',
                description: 'The fields on offer come from Personnel → Settings → Identification.',
                actionLabel: 'Add numbers',
              }
        }
      >
        {record.identifications.length === 0 ? null : (
          <PreviewTable
            variant="table-display"
            rows={record.identifications.map((value) => ({
              // The type's name is a LIVE view of the catalog: renaming the type corrects every
              // record that holds one.
              label: value.typeName,

              /*
                THE NUMBER IS NOT ON THE RECORD. It lives in rsys_pii from 17 Sep 2026, and
                reading one is an audited reveal — so the card states what it holds locally and
                says plainly that the number is elsewhere, rather than rendering an em dash,
                which would read as a card nobody filled in.

                The issue details qualify the card rather than being facts of their own, so they
                sit inline the way an age sits beside a date of birth.
              */
              value: (
                <span className="text-slate-500">
                  Number hidden
                  {value.placeIssued || value.dateIssued || value.validUntil ? (
                    <span className="ml-1 text-slate-400">
                      (
                      {[
                        value.placeIssued,
                        value.dateIssued ? `issued ${formatDateOnly(value.dateIssued)}` : null,
                        value.validUntil ? `valid to ${formatDateOnly(value.validUntil)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      )
                    </span>
                  ) : null}
                </span>
              ),
            }))}
          />
        )}
      </ProfileCard>
    </ProfileSection>
  );
}

/**
 * Service in the military or the police — a SECTION of Basic info, not a panel.
 *
 * It had its own nav entry until 17 Sep 2026, conditional on the flag, so the rail was a
 * different length on two employees' profiles. As a card it keeps the same conditionality
 * where conditionality belongs — a record that never served draws nothing rather than a
 * column of em dashes — while the panel list stays the same on every record.
 *
 * Its own card and its own Edit, because one card is one section is one modal: folding these
 * seven fields into the Personal card would put a second Edit in that cog, and the seam it
 * would be split along is the one `<ProfileCard>`'s rule refuses.
 */
function MilitaryCard({ record, onEdit }: { record: PersonnelDetail; onEdit: () => void }) {
  return (
    <ProfileCard
      id="personnel-military"
      title="Military service"
      onEdit={onEdit}
      editPermission="personnel.update"
    >
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Branch',
              value: record.militaryBranch ? MILITARY_BRANCH_LABELS[record.militaryBranch] : null,
            },
            {
              label: 'Status',
              value: record.militaryServiceStatus
                ? MILITARY_SERVICE_STATUS_LABELS[record.militaryServiceStatus]
                : null,
            },
            { label: 'Rank', value: record.militaryRank },
            {
              label: 'Serial / service number',
              value: record.militaryServiceNumber ? (
                <span className="font-mono">{record.militaryServiceNumber}</span>
              ) : null,
            },
            { label: 'Unit / assignment', value: record.militaryUnit },
            {
              label: 'Entered service',
              value: record.militaryDateEnteredService
                ? formatDateOnly(record.militaryDateEnteredService)
                : null,
            },
            {
              label: 'Separated',
              value: record.militaryDateSeparated
                ? formatDateOnly(record.militaryDateSeparated)
                : null,
            },
          ]}
      />
    </ProfileCard>
  );
}

/**
 * The SERVICE RECORD — every post this employee has held, led by the one they hold now.
 *
 * **One panel, two sections.** They were `Employment` and `Assignments`, and a reader
 * correcting a hire date and a reader recording a new post were sent to different nav entries
 * for one question — what does this person do here. Product call, 15 Sep 2026.
 *
 * **Renamed from `Employment/Assignments` the same day.** That is what a Philippine employer
 * calls this section of a 201 file; a panel named after two of its parts joined by a slash was
 * naming the implementation rather than the document. The panel KEY is still `employment`,
 * because it is the create wizard's step key and the panel-mirror rule keys the two together.
 *
 * **The Employment CARD is gone, and a banner states the current primary post instead.**
 * Product call, 15 Sep 2026. The card held three rows and the panel paid a screen of height
 * for them: the employee number was already printed in the header three inches above it — the
 * same value in two slots, which is the rule §8 sets — and the hire date and the employment
 * type are record-level facts true on every panel rather than facts about this one. Those two
 * moved to the header's `secondary` line and their Edit moved to the header's cog, where an
 * action about the whole record belongs.
 *
 * What is left is what the panel is actually for: which post they hold now, and the history
 * under it.
 */
function ServiceRecordPanel({
  record,
  primary,
  openCount,
  onRecord,
  onEnd,
}: {
  record: PersonnelDetail;
  /** The open line carrying `isPrimary`, or null when no open line does. */
  primary: PersonnelServiceRecord | null;
  /** How many lines are open at all — what separates "none held" from "none primary". */
  openCount: number;
  onRecord: () => void;
  onEnd: (serviceRecord: PersonnelServiceRecord) => void;
}) {
  return (
    <ProfileSection>
      {/*
        The BANNER is inside the gate with the table, and it has to be: it states a unit, a
        position title and an item number, every one of them the plantilla's. With the
        employment facts moved to the header there is nothing left on this panel that is this
        record's own, so there is no longer anything the gate would wrongly hide — which is
        what the gate used to sit below the card to avoid.
      */}
      <PermissionGate permission="plantilla.read">
        <PrimaryPostBanner record={record} primary={primary} openCount={openCount} />

        <ServiceRecordsSection record={record} onRecord={onRecord} onEnd={onEnd} />
      </PermissionGate>
    </ProfileSection>
  );
}

/**
 * Which post this employee holds right now, in one line above the history.
 *
 * **It states a fact rather than a lifecycle, so it carries no action.** The way to change
 * what it says is to record a line of service, and that Create is already in the toolbar
 * directly beneath it — a second button for it here would be one action in two places on one
 * screen.
 *
 * **Four states, and all four render.** A banner that appears only when something is wrong
 * teaches the reader to skip the region, and then it is missed on the day it matters — which
 * is `<ProfileBanner>`'s own rule.
 */
function PrimaryPostBanner({
  record,
  primary,
  openCount,
}: {
  record: PersonnelDetail;
  primary: PersonnelServiceRecord | null;
  openCount: number;
}) {
  /*
    A DELETED employee holds nothing, whatever rows survive on the record: deleting one closes
    every open line, so a banner naming a post would describe something nobody holds.
  */
  if (!record.isActive) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="slate"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message="This employee is deleted. Every line of service was closed with them; restore the record before recording another."
      />
    );
  }

  if (primary) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="blue"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message={
          <>
            Currently{' '}
            <span className="font-medium">
              {primary.positionTitle} — {primary.orgUnitName}
            </span>{' '}
            {/* An item number is a machine value, so it takes the mono face here too. */}
            on item <span className="font-mono">{primary.itemNumber}</span>, held since{' '}
            {formatDateOnly(primary.effectiveFrom)}.
            {/*
              Said only when it is true and worth saying. On the ordinary record this is the
              only open line, and naming it "primary" there would be noise; past one, which of
              them payroll reads is the whole reason the flag exists.
            */}
            {openCount > 1
              ? ` This is their primary post of ${openCount} — their unit, their grade and their reporting line are all read from it.`
              : ''}
          </>
        }
      />
    );
  }

  /*
    AMBER for both of the remaining states, and they are different facts rather than one.

    Open lines with no primary among them is the worse of the two: the record reports a unit, a
    grade and a reporting line off the primary one, so all three are unanswerable until a reader
    marks one. It is reachable — ending the primary line leaves the others open — which is why
    it is stated rather than assumed away.
  */
  if (openCount > 0) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="amber"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message={`This employee holds ${openCount} ${openCount === 1 ? 'post' : 'posts'} and none is marked primary. Their unit, grade and reporting line are read from the primary one.`}
      />
    );
  }

  return (
    <ProfileBanner
      id="personnel-primary-post"
      tone="amber"
      icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
      message="This employee holds no post. Record one to give the record a unit, a grade and a reporting line."
    />
  );
}

/**
 * The health card this employee is covered by.
 *
 * Its own panel since 15 Sep 2026. It was two rows on the Employment card while the create
 * form collected it under Identification — a field entered on one panel and corrected on
 * another, which is the mirror rule broken in the one place nobody checks. A health card is
 * neither an identity document nor a term of appointment; it is a benefit.
 */
function HmoPanel({ record, onEdit }: { record: PersonnelDetail; onEdit: () => void }) {
  return (
    <ProfileSection>
      <ProfileCard
        id="personnel-hmo"
        title="HMO"
        onEdit={onEdit}
        editPermission="personnel.update"
        /*
          An empty section states the absence and offers the way out of it, rather than leaving
          a column of em dashes and a cog the reader has to guess at.
        */
        empty={
          record.hmoProviderName || record.hmoCardNumber
            ? undefined
            : {
                title: 'No HMO recorded',
                description: 'Add the provider and the card number once the employee is enrolled.',
              }
        }
      >
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'HMO provider', value: record.hmoProviderName },
            {
              label: 'HMO card number',
              // A card number is a CODE, so it reads in the mono face like every other.
              value: record.hmoCardNumber ? (
                <span className="font-mono">{record.hmoCardNumber}</span>
              ) : null,
            },
          ]}
        />
      </ProfileCard>
    </ProfileSection>
  );
}

/**
 * The account this employee signs in with, when they have one.
 *
 * The account is not subject PII — §4.1's standing exception — so its alias and email render
 * here directly rather than through the identity service.
 */
/**
 * Which account this employee signs in as.
 *
 * **The Edit changes the LINK, not the account.** `userId` is a column on this record, so
 * attaching and detaching is `personnel.update` and belongs here. Everything about the account
 * itself — its alias, its email, its role, its lockout — is the users module's, and the row
 * links out to it rather than editing another module's record in place.
 */
function AccountPanel({ record, onEdit }: { record: PersonnelDetail; onEdit: () => void }) {
  return (
    <ProfileSection>
      <ProfileCard
        id="personnel-account"
        title="User account"
        onEdit={onEdit}
        editPermission="personnel.update"
        /*
          No account is a state with a way out of it, so the card says both. The banner's
          button is this card's own Edit — the reader is already looking at the section, and
          the alternative was a sentence telling them to go and find the cog themselves.
        */
        empty={
          record.userId
            ? undefined
            : {
                title: 'No account linked',
                description:
                  'This employee cannot sign in. Link an existing account, or create one from the personnel form.',
                actionLabel: 'Link an account',
              }
        }
      >
        {record.userId ? (
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Alias',
                /*
                  A link, because the account is a record of its own and this is the only way
                  to reach it. Black with an underline on hover, never a blue link colour —
                  and gated on the key its destination is gated on, falling back to the plain
                  alias rather than vanishing: which account this is stays worth stating.
                */
                value: (
                  <PermissionGate
                    permission="users.read"
                    fallback={<span>{record.userAlias}</span>}
                  >
                    <Link
                      to={`/users/${record.userId}`}
                      className="font-medium text-slate-800 hover:underline"
                    >
                      {record.userAlias}
                    </Link>
                  </PermissionGate>
                ),
              },
            ]}
          />
        ) : null}
      </ProfileCard>
    </ProfileSection>
  );
}

/**
 * Every position this person has held, open and closed.
 *
 * A HISTORY rather than a current-position field, which is why closed rows stay: a plantilla
 * item keeps the record of who held it and when, and a transfer is a new row rather than an
 * edit to the old one.
 */
/**
 * The posts this employee holds and has held.
 *
 * **A section rather than a panel since 15 Sep 2026.** It draws no `<ProfileSection>` of its
 * own: `<ServiceRecordPanel>` owns the panel and renders this under the employment facts, because
 * "what does this person do here" was being answered in two places a reader had to choose
 * between.
 */
function ServiceRecordsSection({
  record,
  onRecord,
  onEnd,
}: {
  record: PersonnelDetail;
  onRecord: () => void;
  onEnd: (serviceRecord: PersonnelServiceRecord) => void;
}) {
  /*
    The table's CREATE, in the toolbar's leading slot — the same position, variant and icon
    the index page uses, because adding a service record is adding a record and a reader should
    not have to learn a second shape for it. It used to sit beside the section heading, which
    is a place nothing else in the product puts Create.

    Hidden without the key, never disabled — a pure permission gate hides. Disabled here is
    for STATE: a deleted employee takes no new service record, and the tooltip says so rather than
    leaving a dead button.
  */
  /*
    Deleted rows are hidden at rest and the cog lifts it — the same arrangement every list in
    the product has. NOT persisted: a view that quietly kept showing deleted rows from
    yesterday is how a deleted record gets treated as live.
  */
  const [showDeleted, setShowDeleted] = useState(false);

  const serviceRecords = useMemo(
    () => (showDeleted ? record.serviceRecords : record.serviceRecords.filter((a) => a.isActive)),
    [record.serviceRecords, showDeleted],
  );

  /*
    Searched, sorted and paged IN MEMORY, which is correct here for the reason it is correct on
    the org chart: the rule against client-side filtering is about a paged list whose filter
    silently applies to the rows on screen and not the hundreds behind them. These arrive whole
    with the record — one endpoint, every row — so the filter sees everything there is.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? serviceRecords.filter((a) =>
            [a.positionTitle, a.orgUnitName, a.itemNumber, a.appointmentNatureName, a.remarks]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : serviceRecords;

      const field = query.sort?.replace(/^-/, '') ?? 'effectiveFrom';
      const descending = query.sort?.startsWith('-') ?? true;

      const sorted = [...matched].sort((a, b) => {
        const pick = (row: PersonnelServiceRecord) =>
          field === 'positionTitle'
            ? row.positionTitle
            : field === 'orgUnitName'
              ? row.orgUnitName
              : field === 'itemNumber'
                ? row.itemNumber
                : row.effectiveFrom;

        return String(pick(a)).localeCompare(String(pick(b))) * (descending ? -1 : 1);
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [serviceRecords],
  );

  const state = useDataTable<PersonnelServiceRecord>({
    fetchPage,
    pageSize: 10,

    // Most recent first: a service record history is read from the current post backwards.
    defaultSort: '-effectiveFrom',
  });

  const columns: DataColumn<PersonnelServiceRecord>[] = [
    {
      key: 'orgUnitName',
      header: 'Unit',
      width: 200,
      sortable: true,
      render: (a) => <TextCell value={a.orgUnitName} />,
    },
    {
      key: 'positionTitle',
      header: 'Position',
      width: 220,
      sortable: true,
      render: (a) => <TextCell value={a.positionTitle} />,
    },
    {
      key: 'itemNumber',
      header: 'Item',
      width: 120,
      sortable: true,
      render: (a) => <TextCell value={a.itemNumber} mono />,
    },
    {
      // Calendar dates, so the conversion-free formatter: an effectivity date names a day,
      // and the instant path would shift it for a reader west of UTC.
      key: 'effectiveFrom',
      header: 'From',
      width: 130,
      sortable: true,
      render: (a) => <TextCell value={formatDateOnly(a.effectiveFrom)} mono />,
    },
    {
      key: 'effectiveTo',
      header: 'To',
      width: 130,
      // A blank, not an em dash: an open appointment has no end date rather than a missing one.
      render: (a) => <TextCell value={a.effectiveTo ? formatDateOnly(a.effectiveTo) : null} mono />,
    },
    {
      /*
        The appointment behind the line of service. CALENDAR dates, so the conversion-free
        formatter and `mono` — a date is a machine value on every surface in this product.

        They sit AFTER the period rather than before it: the reader scans this table for when
        somebody moved, and the period is what answers that. The instrument's own date is what
        they come back for.
      */
      key: 'appointmentDate',
      header: 'Appointed',
      width: 130,
      render: (a) => (
        <TextCell value={a.appointmentDate ? formatDateOnly(a.appointmentDate) : null} mono />
      ),
    },
    {
      key: 'assumptionDate',
      header: 'Assumed',
      width: 130,
      render: (a) => (
        <TextCell value={a.assumptionDate ? formatDateOnly(a.assumptionDate) : null} mono />
      ),
    },
    {
      /*
        A LIVE view resolved by the server, never a stored copy — correcting the vocabulary
        corrects every line filed under it. Plain text, not mono: a nature of appointment is a
        word somebody chose, not a machine value.
      */
      key: 'appointmentNatureName',
      header: 'Nature',
      width: 170,
      render: (a) => <TextCell value={a.appointmentNatureName ?? null} />,
    },
    {
      key: 'isPrimary',
      header: 'Primary',
      width: 100,
      render: (a) => <TextCell value={a.isPrimary ? 'Yes' : null} />,
    },
    {
      /*
        The note on the line of service. Collected by both write paths and rendered nowhere —
        no column, and so no row in the preview either, which is built from these same
        columns. A remark could be written and never read back.
      */
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      render: (a) => <TextCell value={a.remarks ?? null} />,
    },
    /*
      CREATION METADATA IS THE SHELL'S, and this panel used to declare its own.

      `<DataTablePanel>` appends Created · Created by · Latest update from the `otherInfo`
      below — `buildAuditColumns` is the one definition both table shells read — so the three
      declared here rendered a SECOND time beside them: six audit columns in the table, and
      six rows in the preview modal, which is built from these columns. The same value in two
      slots, on every service record in the product.

      Everything the copies carried is in the shared definition: not sortable, `Created by` at
      the 13px of the date beside it, and `Latest update` blank rather than an em dash on a row
      never touched since creation.
    */
    {
      /*
        Status is always the last data column, and the three states are mutually exclusive
        with DELETED tested first — the same precedence the users list gives a lockout. A
        deleted row that read "open" would describe an appointment nobody holds.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (a) =>
        !a.isActive ? (
          <StatusText tone="inactive">deleted</StatusText>
        ) : a.effectiveTo ? (
          <StatusText tone="inactive">ended</StatusText>
        ) : (
          <StatusText tone="active">open</StatusText>
        ),
    },
  ];

  return (
    <>
      {/*
        A TABLE, not a card per service record. A history is read by scanning down a column — when
        did they move, what did they hold — and a stack of cards makes that a scroll through
        label/value pairs. It owes the standard contract either way: search, sort, resize, page.
      */}
      <DataTablePanel<PersonnelServiceRecord>
        id="personnel-service-records"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Service record', size: 'lg' }}
        columns={columns}
        rowKey={(a) => a.id}
        searchPlaceholder="Search service records"
        onCreate={onRecord}
        createPermission="personnel.assign"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before recording a service record.'
        }
        /*
          The kebab's entries. The shell owns the gutter and the menu, so this is only what
          goes in it — and the same array feeds the table and the list view.

          Only an OPEN appointment can be ended, but "already ended" is a STATE, so the entry
          is DISABLED WITH A REASON rather than dropped. The sentence is the server's own:
          `EndServiceRecordAsync` answers with Conflict("That service record has already ended.").
        */
        rowActions={(a) => [
          {
            key: `end-${a.id}`,
            label: 'End service record',
            permission: 'personnel.assign',
            disabledReason: a.effectiveTo
              ? `This service record already ended on ${formatDateOnly(a.effectiveTo)}.`
              : null,
            onClick: () => onEnd(a),
          },
        ]}
        /*
          The audit trail the columns do not carry — the updater, and the ids. Supplying it
          also earns the Table/List switch on the cog, which the panel offers only when it
          has provenance to lead a list row with.
        */
        otherInfo={(a) => ({
          createdAt: a.createdAt,
          createdByName: a.createdByName,
          updatedAt: a.updatedAt,
          updatedByName: a.updatedByName,
        })}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="This employee holds no post yet."
        emptySearchMessage="No service record matches that search."
      />
    </>
  );
}

function ContactsPanel({ record }: { record: PersonnelDetail }) {
  const names = usePersonNames(record.emergencyContacts.map((c) => c.personId));

  if (record.emergencyContacts.length === 0) {
    return (
      <ProfileSection>
        <EmptyState message="No emergency contact recorded." />
      </ProfileSection>
    );
  }

  return (
    <ProfileSection>
      <div className="flex flex-col gap-4">
        {record.emergencyContacts.map((contact) => (
          <ProfileCard
            key={contact.id}
            id={`personnel-contact-${contact.id}`}
            title={names.nameOf(contact.personId) ?? (names.failed ? 'Name unavailable' : '…')}
          >
            <PreviewTable
              variant="table-display"
              rows={[
                { label: 'Relationship', value: contact.relationship },
                {
                  label: 'Contact number',
                  value: <span className="font-mono">{contact.contactNumber}</span>,
                },
                { label: 'Barangay', value: contact.barangay },
                {
                  label: 'City / municipality',
                  value: contact.cityMunicipality,
                },
                { label: 'Province', value: contact.province },
                { label: 'Street address', value: contact.streetAddress },
                { label: 'Primary', value: contact.isPrimary ? 'Yes' : 'No' },
              ]}
            />
          </ProfileCard>
        ))}
      </div>
    </ProfileSection>
  );
}
